import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
} from '@angular/forms';
import { Subject, takeUntil, take, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { IconComponent } from '../../../shared/components/icon/icon.component';
import { CardWrapperComponent } from '../../../shared/components/card-wrapper/card-wrapper.component';
import { VerifyDomainComponent } from '../../../shared/components/verify-domain/verify-domain.component';
import { RegistroBrDomainAvailabilityResponse } from '../../../core/models/validation.model';
import { ProductInfo } from '../../../core/models/product.model';
import { ProductV2, ProductV2Variation } from '../../../core/models/product-v2.model';
import { CartFacade } from '../../../core/store/cart/cart.facade';
import { CartItem } from '../../../core/store/cart/cart.state';
import { ValidationService } from '../../../core/services/validation.service';
import { MicrosoftTenantIdValidationParams } from '../../../core/models/validation.model';
import { UserFacade } from '../../../core/store/user/user.facade';
import { CategoryIconMapperService } from '../../../shared/services/category-icon-mapper.service';
import { I18nPipe } from '../../../shared/pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import { DomainFeedback } from '../../../core/models/domain-feedback.model';
import {
  NO_EXISTING_DOMAIN_VARIATION_UUIDS,
  PRESENCA_DIGITAL_UUIDS,
  REGISTRY_DOMAIN_UUIDS,
  FREE_DOMAIN_INCLUDED_UUIDS,
} from '../../../shared/constants/variation-uuids.constant';
import { isEmailEmpresarialVariationUuid } from '../../../core/utils/cart-domain-suggestion-rules';
import { HOSTING_SITE_REGISTRATION_PRICE } from '../utils/cart-domain-promo-rules';
import { isValidDomain, isValidDomainInput } from '../../../shared/validators/domain.validator';
import { EMAIL_DOMAIN_FIELD_NAME } from '../../../shared/constants/domain-promo.constants';

import { DomainAuthorizationModalComponent } from './domain-authorization-modal/domain-authorization-modal.component';
import { DomainAuthorizationConfirmationModalComponent } from './domain-authorization-confirmation-modal/domain-authorization-confirmation-modal.component';

type ButtonActionType = 'Editar';

const DOMAIN_TYPES = {
  GOOGLE: 'google',
  MICROSOFT: 'microsoft',
} as const;

interface RequiredField {
  name: string;
  label: string;
  type: string;
  value?: string;
}

interface ConfigurableProduct extends ProductInfo {
  hasRequiredFields: boolean;
  selectedVariation: ProductV2Variation;
  baseVariationUuid: string;
  cartItemId: string;
  domainFormKey: string;
  domainFormIndex: number;
  requiredFields: RequiredField[];
  domainType?: 'new' | 'existing';
}

interface UnifiedFormData {
  domainType: 'new' | 'existing';
  domain: string;
  baseVariationUuid: string;
  cartItemId: string;
  variationUuid: string;
  group: 'google' | 'microsoft' | 'com.br' | 'net' | 'com' | 'other';
  grouped: boolean;
}

@Component({
  selector: 'umkt-product-config-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IconComponent,
    CardWrapperComponent,
    VerifyDomainComponent,
    DomainAuthorizationModalComponent,
    DomainAuthorizationConfirmationModalComponent,
    I18nPipe
  ],
  templateUrl: './product-config-card.component.html',
  styleUrl: './product-config-card.component.scss',
})
export class ProductConfigCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() products: ProductInfo[] = [];
  @Input() actionType: ButtonActionType = 'Editar';
  @Input() isEditing = false;
  @Input() isLastStep = false;
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() validateOrderRequest = new EventEmitter<{ isLastStep: boolean }>();

  private i18nService = inject(I18nService);

  public isLoading = false;
  public loadingText = this.i18nService.instant('shared.messages.processing');
  public showAuthorizationModal = false;
  public showAuthorizationConfirmationModal = false;
  public domainNeedingAuthorization: string = '';
  private pendingValidationParams: {
    variationUuid: string;
    group: string;
    isValid: boolean;
  } | null = null;

  public showDomainRegisterQuestion = false;
  private pendingDomainRegisterVariationUuids: string[] = [];
  private pendingDomainRegisterGroup = '';
  public domainRegisterPriceAmount = '';
  public domainRegisterPriceSuffix$!: Observable<string>;

  private includedInOrderVariationUuids: Record<string, boolean> = {};

  public configurableProducts: ConfigurableProduct[] = [];
  public userUuid: string | null = null;
  public domainGroups: { type: string; count: number; label: string }[] = [];
  public domainGroupConfigs: Record<string, boolean> = {};
  public domainValidationResults: Record<string, boolean | null> = {};
  private ownershipManualVerificationErrors: Record<string, boolean> = {};
  private selfConfirmedOwnership: Record<string, boolean> = {};
  private googleDomainNotInUseAcknowledged: Record<string, boolean> = {};
  private previousDomainTypeSelections: Record<string, string> = {};
  private savedDomainsInCart = new Set<string>();
  private static readonly DOMAIN_OWNERSHIP_CACHE_KEY = '_domainOwnershipFlags';
  private static sharedFormDataCache: Record<string, Record<string, unknown>> = {};
  private formDataCache = ProductConfigCardComponent.sharedFormDataCache;

  public static clearSharedFormDataCache(): void {
    ProductConfigCardComponent.sharedFormDataCache = {};
  }

  public static isSelfConfirmedOwnershipCached(domainFormKey: string): boolean {
    return (
      ProductConfigCardComponent.getCachedDomainOwnershipFlags(domainFormKey)
        ?.selfConfirmedOwnership === true
    );
  }

  public static isGoogleDomainNotInUseAcknowledgedCached(
    domainFormKey: string
  ): boolean {
    return (
      ProductConfigCardComponent.getCachedDomainOwnershipFlags(domainFormKey)
        ?.googleDomainNotInUseAcknowledged === true
    );
  }

  private static getCachedDomainOwnershipFlags(domainFormKey: string):
    | {
        selfConfirmedOwnership?: boolean;
        googleDomainNotInUseAcknowledged?: boolean;
      }
    | undefined {
    const flags =
      (ProductConfigCardComponent.sharedFormDataCache[
        ProductConfigCardComponent.DOMAIN_OWNERSHIP_CACHE_KEY
      ] as
        | Record<
            string,
            {
              selfConfirmedOwnership?: boolean;
              googleDomainNotInUseAcknowledged?: boolean;
            }
          >
        | undefined) ?? {};

    return flags[domainFormKey];
  }
  
  // Valores originais salvos ao entrar em modo de edição (para restaurar ao cancelar)
  private originalDomains: Record<string, string> = {};
  private originalDomainTypes: Record<string, 'new' | 'existing'> = {};
  private originalDomainGroupConfigs: Record<string, boolean> = {};
  private originalDomainValidationResults: Record<string, boolean | null> = {};
  private originalSelfConfirmedOwnership: Record<string, boolean> = {};
  private originalGoogleDomainNotInUseAcknowledged: Record<string, boolean> = {};
  private originalPreviousDomainTypeSelections: Record<string, string> = {};

  public domainConfigForm!: FormGroup;
  public productForms!: FormArray;

  private cartFacade = inject(CartFacade);
  private formBuilder = inject(FormBuilder);
  private destroy$ = new Subject<void>();
  private validationService = inject(ValidationService);
  private userFacade = inject(UserFacade);
  private categoryMapper = inject(CategoryIconMapperService);

  // ============================================================================
  // COMPUTED PROPERTIES & GETTERS
  // ============================================================================

  get showCheck(): boolean {
    if (this.isLastStep && this.isEditing) {
      return false;
    }

    if (this.isEditing) {
      return this.isFormValid() && this.areAllDomainsValid();
    }

    if (this.isLastStep) {
      if (!this.hasConfigurableProducts) {
        return true;
      }
      return this.areAllDomainsConfigured() && this.areAllDomainsValid();
    }

    return this.areAllDomainsConfigured();
  }

  get hasConfigurableProducts(): boolean {
    return this.configurableProducts && this.configurableProducts.length > 0;
  }

  private setLoading(
    loading: boolean,
    textKey: string = 'shared.messages.processing', 
    params?: Record<string, string | number>
  ): void {
    this.isLoading = loading;
    this.loadingText = this.i18nService.instant(textKey, params);
  }

  public getButtonTextKey(variationUuid: string): string {
    const formData = this.getUnifiedFormData(variationUuid);
    return formData?.domainType === 'existing'
      ? 'pages.productConfig.domain.verifyExisting'
      : 'pages.productConfig.domain.verify';
  }

  public showFreeDomainLabel(variation: ProductV2Variation | undefined): boolean {
    if (!variation) return false;
    return FREE_DOMAIN_INCLUDED_UUIDS.includes(variation.uuid);
  }

  private normalizeUuid(uuid: unknown): string {
    return String(uuid ?? '').trim().toLowerCase();
  }

  public isRegistryDomainVariationUuid(variationUuid: string | undefined | null): boolean {
    const u = this.normalizeUuid(variationUuid);
    if (!u) return false;
    return REGISTRY_DOMAIN_UUIDS.some(x => this.normalizeUuid(x) === u);
  }

  private resolveDomainFieldName(
    group: string,
    variationUuid?: string | null
  ): string {
    if (isEmailEmpresarialVariationUuid(variationUuid)) {
      return EMAIL_DOMAIN_FIELD_NAME;
    }
    if (group === 'google') return 'google_ws_domain';
    if (group === 'microsoft') return 'microsoft_domain';
    if (group === 'com.br') return 'com_br_domain';
    if (group === 'com') return 'com_domain';
    if (group === 'net') return 'net_domain';
    return '';
  }

  private shouldPersistDomainPreference(domainFieldName: string): boolean {
    return domainFieldName === 'com_br_domain' || domainFieldName === EMAIL_DOMAIN_FIELD_NAME;
  }

  public isExistingDomainPurchaseBlocked(variationUuid: string | undefined | null): boolean {
    if (!variationUuid) return false;
    return NO_EXISTING_DOMAIN_VARIATION_UUIDS.includes(variationUuid);
  }

  public get groupConfigsArray(): FormArray {
    return this.domainConfigForm.get('groupConfigs') as FormArray;
  }

  public get groupedConfigurableProducts(): {
    groupType: string;
    products: ConfigurableProduct[];
  }[] {
    const hasGroupConfig =
      this.domainGroupConfigs['google'] || this.domainGroupConfigs['microsoft'];

    if (!hasGroupConfig) {
      return this.configurableProducts.map(product => ({
        groupType: 'individual',
        products: [product],
      }));
    }

    const groups: { groupType: string; products: ConfigurableProduct[] }[] = [];
    const googleProducts: ConfigurableProduct[] = [];
    const microsoftProducts: ConfigurableProduct[] = [];

    this.configurableProducts.forEach(product => {
      const domainType = this.getProductDomainType(product);

      if (domainType === 'google') {
        googleProducts.push(product);
      } else if (domainType === 'microsoft') {
        microsoftProducts.push(product);
      }
    });

    if (googleProducts.length > 0) {
      groups.push({ groupType: 'google', products: googleProducts });
    }
    if (microsoftProducts.length > 0) {
      groups.push({ groupType: 'microsoft', products: microsoftProducts });
    }
    return groups;
  }

  // ============================================================================
  // FORM MANAGEMENT
  // ============================================================================

  private findFormIndexByVariationUuid(variationUuid: string): number {
    for (let i = 0; i < this.groupConfigsArray.length; i++) {
      const form = this.groupConfigsArray.at(i);
      if (form?.get('variationUuid')?.value === variationUuid) {
        return i;
      }
    }
    return -1;
  }

  public getUnifiedFormData(variationUuid: string): UnifiedFormData | null {
    try {
      const formIndex = this.findFormIndexByVariationUuid(variationUuid);
      if (formIndex === -1) {
        return null;
      }

      const form = this.groupConfigsArray.at(formIndex);
      if (!form) {
        return null;
      }

      const domainTypeValue = form.get('domainType')?.value;
      
      return {
        domainType: domainTypeValue || 'new',
        domain: form.get('domain')?.value || '',
        variationUuid: form.get('variationUuid')?.value || '',
        baseVariationUuid: form.get('baseVariationUuid')?.value || '',
        cartItemId: form.get('cartItemId')?.value || '',
        group: form.get('group')?.value || 'other',
        grouped: form.get('grouped')?.value || false,
      };
    } catch (error) {
      console.error('[DEBUG getUnifiedFormData] Erro:', error, 'para', variationUuid);
      return null;
    }
  }

  public getUnifiedFormDatasByBaseVariationUuid(baseVariationUuid: string): UnifiedFormData[] {
    const results: UnifiedFormData[] = [];
    for (let i = 0; i < this.groupConfigsArray.length; i++) {
      const form = this.groupConfigsArray.at(i);
      if (!form) continue;

      const formBaseVariationUuid = form.get('baseVariationUuid')?.value;
      if (formBaseVariationUuid !== baseVariationUuid) continue;

      const domainTypeValue = form.get('domainType')?.value;
      results.push({
        domainType: domainTypeValue || 'new',
        domain: form.get('domain')?.value || '',
        variationUuid: form.get('variationUuid')?.value || '',
        baseVariationUuid: form.get('baseVariationUuid')?.value || '',
        cartItemId: form.get('cartItemId')?.value || '',
        group: form.get('group')?.value || 'other',
        grouped: form.get('grouped')?.value || false,
      });
    }
    return results;
  }

  public getUnifiedFormDataByBaseVariationUuidAndCartItemIdAndIndex(
    baseVariationUuid: string,
    cartItemId: string,
    domainFormIndex: number
  ): UnifiedFormData | null {
    const all = this.getUnifiedFormDatasByBaseVariationUuid(baseVariationUuid);
    const filtered = all.filter(fd => String(fd.cartItemId ?? '') === String(cartItemId ?? ''));
    return filtered[domainFormIndex] ?? null;
  }

  public getUnifiedFormDataByBaseVariationUuidAndIndex(
    baseVariationUuid: string,
    domainFormIndex: number
  ): UnifiedFormData | null {
    return this.getUnifiedFormDatasByBaseVariationUuid(baseVariationUuid)[domainFormIndex] ?? null;
  }

  public updateUnifiedFormData(
    variationUuid: string,
    updates: Partial<UnifiedFormData>,
    options?: { emitEvent?: boolean }
  ): void {
    try {
      const formIndex = this.findFormIndexByVariationUuid(variationUuid);
      if (formIndex === -1) return;

      const form = this.groupConfigsArray.at(formIndex);
      if (form) {
        // Alguns UUIDs nao podem ir para "existing" mesmo se o estado vier do cache/edição.
        const baseVariationUuid = form.get('baseVariationUuid')?.value;
        const emitEvent = options?.emitEvent !== false;
        if (
          this.isExistingDomainPurchaseBlocked(baseVariationUuid) &&
          updates.domainType === 'existing'
        ) {
          form.patchValue({ ...updates, domainType: 'new' }, { emitEvent });
          return;
        }

        form.patchValue(updates, { emitEvent });
      }
    } catch (error) {
      console.error('Erro ao atualizar formulário unificado:', error);
    }
  }

  // ============================================================================
  // DOMAIN UTILITIES
  // ============================================================================

  public getDomainSuffixByGroup(
    group: string,
    domainType?: 'new' | 'existing' | string | null
  ): string {
    switch (group) {
      case 'google':
        // new: só .com.br (registro de domínio); existing: qualquer sufixo (hostname completo)
        return domainType === 'new' ? '.com.br' : '';
      case 'microsoft':
        return '.onmicrosoft.com';
      case 'com.br':
        return '.com.br';
      case 'net':
        return '.net';
      case 'com':
        return '.com';
      default:
        return '.com';
    }
  }

  public getDomainSuffixForVariation(variationUuid: string): string {
    const formData = this.getUnifiedFormData(variationUuid);
    return this.getDomainSuffixByGroup(formData?.group || '', formData?.domainType);
  }

  public isDomainValidForForm(variationUuid: string): boolean {
    const formData = this.getUnifiedFormData(variationUuid);
    return Boolean(formData?.domain && formData.domain.trim().length > 0);
  }

  public getButtonTextForForm(variationUuid: string): string {
    return this.i18nService.instant(this.getButtonTextKey(variationUuid));
  }

  public isVerifyButtonDisabled(variationUuid: string): boolean {
    const formData = this.getUnifiedFormData(variationUuid);
    const domain = formData?.domain || '';

    return (
      !domain ||
      domain.trim().length === 0 ||
      !isValidDomainInput(domain, this.getDomainSuffixForVariation(variationUuid)) ||
      this.domainValidationResults[variationUuid] === false ||
      this.isDomainDuplicated(variationUuid) ||
      this.isDomainRegisterConfirmationPending(variationUuid)
    );
  }

  public getCustomErrorMessage(variationUuid: string): string | undefined {
    if (this.domainValidationResults[variationUuid] === false && this.isDomainDuplicated(variationUuid)) {
      return this.i18nService.instant('pages.productConfig.domain.errorDuplicate');
    }
    return undefined;
  }

  public getVerifyDomainFeedback(variationUuid: string): DomainFeedback | undefined {
    if (this.ownershipManualVerificationErrors[variationUuid]) {
      const formData = this.getUnifiedFormData(variationUuid);
      if (!formData) return undefined;

      const suffix = this.getDomainSuffixByGroup(formData.group, formData.domainType);
      const domainValue = formData.domain || '';
      const completeDomain = `${domainValue}${suffix}`.trim();

      const fullMessage = this.i18nService.instant(
        'pages.productConfig.domain.ownershipManualVerification',
        { domain: completeDomain || domainValue }
      );

      const [rawTitle, ...rawDescriptionParts] = fullMessage.split('\n');
      const title = (rawTitle || '').trim().replace(/\.$/, '');
      const description = rawDescriptionParts.join('\n').trim().replace(/\.$/, '');

      return {
        type: 'warning',
        title,
        description,
        isDomainFree: false,
      };
    }

    const formData = this.getUnifiedFormData(variationUuid);
    if (!formData) return undefined;

    if (
      this.includedInOrderVariationUuids[variationUuid] &&
      this.domainValidationResults[variationUuid] === true &&
      formData.domainType === 'new'
    ) {
      const group = formData.group;
      if (group === 'microsoft') return undefined;

      const product = this.configurableProducts.find(
        p => p.domainFormKey === variationUuid
      );
      // Google nunca tem domínio grátis; Presença Digital com free label também não mostra este feedback.
      const shouldShowFreeDomain =
        group !== 'google' && this.showFreeDomainLabel(product?.selectedVariation);
      if (shouldShowFreeDomain) return undefined;

      const suffix = this.getDomainSuffixByGroup(group, formData.domainType);
      const domainValue = formData.domain || '';
      const completeDomain = `${domainValue}${suffix}`.trim();

      return {
        type: 'success',
        title: this.i18nService.instant('pages.productConfig.domain.domainIncludedInOrderTitle'),
        description: this.i18nService.instant(
          'pages.productConfig.domain.domainIncludedInOrderDescription',
          { domain: completeDomain }
        ),
        isDomainFree: false,
      };
    }

    // Mantém o erro de duplicidade via `customErrorMessage` (o feedback vai sobrescrever).
    if (this.isDomainDuplicated(variationUuid)) return undefined;

    const group = formData.group;
    if (group === 'microsoft') return undefined;
    // Google "new" (antes do confirm de registro) usa o feedback padrão do verify-domain.
    if (group === 'google' && formData.domainType !== 'existing') return undefined;

    if (this.domainValidationResults[variationUuid] === false) {
      const suffix = this.getDomainSuffixByGroup(group, formData.domainType);
      const domainValue = formData.domain || '';
      const completeDomain = `${domainValue}${suffix}`.trim();

      // Para domínio existing: se o backend retornar available/valid=true, então o domínio NÃO está registrado.
      if (formData.domainType === 'existing') {
        return {
          type: 'error',
          title: this.i18nService.instant(
            'pages.productConfig.domain.existingDomainNotRegisteredTitle'
          ),
          description: this.i18nService.instant(
            'pages.productConfig.domain.existingDomainNotRegisteredDescription',
            { domain: completeDomain || domainValue }
          ),
          isDomainFree: false,
        };
      }

      return {
        type: 'warning',
        title: 'Domínio já registrado',
        description: this.i18nService.instant('components.verifyDomain.errorUnavailable'),
        isDomainFree: false,
      };
    }

    if (this.domainValidationResults[variationUuid] === true && formData.domainType === 'new') {
      const product = this.configurableProducts.find(
        p => p.domainFormKey === variationUuid
      );
      // Google nunca tem domínio grátis.
      const shouldShowFreeDomain =
        group !== 'google' && this.showFreeDomainLabel(product?.selectedVariation);

      if (shouldShowFreeDomain) {
        const suffix = this.getDomainSuffixByGroup(group, formData.domainType);
        const domainValue = formData.domain || '';
        const completeDomain = `${domainValue}${suffix}`.trim();

        return {
          type: 'success',
          title: this.i18nService.instant('pages.productConfig.domain.freeDomainAvailableTitle'),
          description: this.i18nService.instant(
            'pages.productConfig.domain.freeDomainAvailableDescription',
            { domain: completeDomain }
          ),
          isDomainFree: true,
        };
      }

      const suffix = this.getDomainSuffixByGroup(group, formData.domainType);
      const domainValue = formData.domain || '';
      const completeDomain = `${domainValue}${suffix}`.trim();

      return {
        type: 'success',
        title: this.i18nService.instant('pages.productConfig.domain.newDomainAvailableTitle'),
        description: this.i18nService.instant(
          'pages.productConfig.domain.newDomainAvailableDescription',
          { domain: completeDomain }
        ),
        isDomainFree: false,
      };
    }

    if (this.domainValidationResults[variationUuid] === true && formData.domainType === 'existing') {
      return {
        type: 'success',
        title: this.i18nService.instant('pages.productConfig.domain.ownershipConfirmedTitle'),
        description: this.i18nService.instant('pages.productConfig.domain.ownershipConfirmedDescription'),
        isDomainFree: false,
      };
    }

    return undefined;
  }

  public getCompleteDomainForVariation(variationUuid: string): string {
    const formData = this.getUnifiedFormData(variationUuid);
    if (!formData) return '';

    const suffix = this.getDomainSuffixByGroup(formData.group, formData.domainType);
    const domainValue = formData.domain || '';
    return `${domainValue}${suffix}`.trim();
  }

  public hasOwnershipManualVerificationError(variationUuid: string): boolean {
    return this.ownershipManualVerificationErrors[variationUuid] === true;
  }

  public isSelfConfirmedOwnership(domainFormKey: string | undefined): boolean {
    if (!domainFormKey) return false;
    if (this.selfConfirmedOwnership[domainFormKey] === true) {
      return true;
    }

    return ProductConfigCardComponent.isSelfConfirmedOwnershipCached(domainFormKey);
  }

  public shouldShowGoogleDomainNotInUseWarning(variationUuid: string): boolean {
    const formData = this.getUnifiedFormData(variationUuid);
    if (!formData) return false;

    return (
      formData.group === 'google' &&
      formData.domainType === 'existing' &&
      this.domainValidationResults[variationUuid] === true &&
      this.isSelfConfirmedOwnership(variationUuid)
    );
  }

  public isGoogleDomainNotInUseAcknowledged(domainFormKey: string | undefined): boolean {
    if (!domainFormKey) return false;
    if (this.googleDomainNotInUseAcknowledged[domainFormKey] === true) {
      return true;
    }

    return ProductConfigCardComponent.isGoogleDomainNotInUseAcknowledgedCached(
      domainFormKey
    );
  }

  public onGoogleDomainNotInUseAcknowledgedChange(
    variationUuid: string,
    event: Event
  ): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.setGoogleDomainNotInUseAcknowledged(variationUuid, checked);
  }

  private setSelfConfirmedOwnership(variationUuid: string, confirmed: boolean): void {
    this.selfConfirmedOwnership[variationUuid] = confirmed;

    const formData = this.getUnifiedFormData(variationUuid);
    if (formData?.grouped) {
      this.configurableProducts.forEach(product => {
        const productFormData = this.getUnifiedFormData(product.domainFormKey);
        if (productFormData?.group === formData.group && productFormData.grouped) {
          this.selfConfirmedOwnership[product.domainFormKey] = confirmed;
        }
      });
    }

    this.persistDomainOwnershipFlags();
  }

  private setGoogleDomainNotInUseAcknowledged(
    variationUuid: string,
    acknowledged: boolean
  ): void {
    this.googleDomainNotInUseAcknowledged[variationUuid] = acknowledged;

    const formData = this.getUnifiedFormData(variationUuid);
    if (formData?.grouped) {
      this.configurableProducts.forEach(product => {
        const productFormData = this.getUnifiedFormData(product.domainFormKey);
        if (productFormData?.group === formData.group && productFormData.grouped) {
          this.googleDomainNotInUseAcknowledged[product.domainFormKey] = acknowledged;
        }
      });
    }

    this.persistDomainOwnershipFlags();
  }

  private persistDomainOwnershipFlags(): void {
    const activeKeys = new Set(this.configurableProducts.map(product => product.domainFormKey));
    const existing =
      (this.formDataCache[ProductConfigCardComponent.DOMAIN_OWNERSHIP_CACHE_KEY] as
        | Record<
            string,
            {
              selfConfirmedOwnership?: boolean;
              googleDomainNotInUseAcknowledged?: boolean;
            }
          >
        | undefined) ?? {};

    const flags = { ...existing };
    Object.keys(flags).forEach(key => {
      if (!activeKeys.has(key)) {
        delete flags[key];
      }
    });

    this.configurableProducts.forEach(product => {
      const key = product.domainFormKey;
      // Usa is* para não sobrescrever cache true com maps vazios após recreate/remount.
      flags[key] = {
        selfConfirmedOwnership: this.isSelfConfirmedOwnership(key),
        googleDomainNotInUseAcknowledged:
          this.isGoogleDomainNotInUseAcknowledged(key),
      };
    });

    this.formDataCache[ProductConfigCardComponent.DOMAIN_OWNERSHIP_CACHE_KEY] = flags;
  }

  private restoreDomainOwnershipFlags(): void {
    const flags =
      (this.formDataCache[ProductConfigCardComponent.DOMAIN_OWNERSHIP_CACHE_KEY] as
        | Record<
            string,
            {
              selfConfirmedOwnership?: boolean;
              googleDomainNotInUseAcknowledged?: boolean;
            }
          >
        | undefined) ?? {};

    this.configurableProducts.forEach(product => {
      const key = product.domainFormKey;
      const cached = flags[key];
      if (!cached) return;

      this.selfConfirmedOwnership[key] = cached.selfConfirmedOwnership === true;
      this.googleDomainNotInUseAcknowledged[key] =
        cached.googleDomainNotInUseAcknowledged === true;
    });
  }

  private clearSavedDomainStateForVariation(variationUuid: string): void {
    this.savedDomainsInCart.delete(variationUuid);
  }

  private clearSavedDomainStateForGroup(group: string): void {
    const groupedProducts = this.configurableProducts.filter(product => {
      const formData = this.getUnifiedFormData(product.domainFormKey);
      return formData?.group === group && formData.grouped;
    });

    groupedProducts.forEach(product => {
      this.clearSavedDomainStateForVariation(product.domainFormKey);
    });
  }

  // ============================================================================
  // DOMAIN EVENT HANDLERS
  // ============================================================================

  private shouldIgnoreStaleDomainEvent(domain: string, variationUuid: string): boolean {
    const next = (domain ?? '').trim();
    const current = (this.getDomainValue(variationUuid) ?? '').trim();
    if (next === current) {
      return true;
    }

    // O input interno do verify-domain começa vazio antes do writeValue.
    if (!next && current) {
      return true;
    }

    return false;
  }

  public onDomainInput(event: Event, variationUuid: string): void {
    const target = event.target as HTMLInputElement;
    const domain = target.value;
    if (this.shouldIgnoreStaleDomainEvent(domain, variationUuid)) {
      return;
    }

    // Se o usuário altera o domínio, a pergunta de registro deve desaparecer
    if (this.isDomainRegisterConfirmationPending(variationUuid)) {
      this.closeDomainRegisterConfirmation();
    }

    this.includedInOrderVariationUuids[variationUuid] = false;
    this.clearSavedDomainStateForVariation(variationUuid);

    this.updateUnifiedFormData(variationUuid, { domain });
    this.domainValidationResults[variationUuid] = null;
    this.setOwnershipManualVerificationError(variationUuid, false);
    this.setSelfConfirmedOwnership(variationUuid, false);
    this.setGoogleDomainNotInUseAcknowledged(variationUuid, false);

    this.updateFormDataCache(variationUuid, { domain });

    const formData = this.getUnifiedFormData(variationUuid);
    if (formData?.grouped) {
      this.clearSavedDomainStateForGroup(formData.group);
      this.syncGroupedProducts(formData.group, { domain }, variationUuid);
    } else {
      this.validateDomainUniqueness(variationUuid, domain);
    }
  }

  public onDomainChange(domain: string, variationUuid: string): void {
    if (this.shouldIgnoreStaleDomainEvent(domain, variationUuid)) {
      return;
    }
    // Se o usuário altera o domínio, a pergunta de registro deve desaparecer
    if (this.isDomainRegisterConfirmationPending(variationUuid)) {
      this.closeDomainRegisterConfirmation();
    }

    this.includedInOrderVariationUuids[variationUuid] = false;
    this.clearSavedDomainStateForVariation(variationUuid);

    this.updateUnifiedFormData(variationUuid, { domain });
    this.domainValidationResults[variationUuid] = null;
    this.setOwnershipManualVerificationError(variationUuid, false);
    this.setSelfConfirmedOwnership(variationUuid, false);
    this.setGoogleDomainNotInUseAcknowledged(variationUuid, false);
    this.updateFormDataCache(variationUuid, { domain, validationResult: null });

    const formData = this.getUnifiedFormData(variationUuid);
    if (formData?.grouped) {
      this.clearSavedDomainStateForGroup(formData.group);
      this.syncGroupedProducts(formData.group, { domain }, variationUuid);
    } else {
      this.validateDomainUniqueness(variationUuid, domain);
    }
  }

  public onConfirmDomainOwnership(variationUuid: string): void {
    const formData = this.getUnifiedFormData(variationUuid);
    if (!formData) return;

    this.setOwnershipManualVerificationError(variationUuid, false);
    this.setSelfConfirmedOwnership(variationUuid, true);
    this.setGoogleDomainNotInUseAcknowledged(variationUuid, false);
    this.setDomainValidationResult(variationUuid, formData.group, true);
  }

  public onChangeDomainOwnership(variationUuid: string): void {
    const formData = this.getUnifiedFormData(variationUuid);
    if (!formData) return;

    this.setOwnershipManualVerificationError(variationUuid, false);
    this.setSelfConfirmedOwnership(variationUuid, false);
    this.setGoogleDomainNotInUseAcknowledged(variationUuid, false);
    this.clearSavedDomainStateForVariation(variationUuid);
    if (formData.grouped) {
      this.clearSavedDomainStateForGroup(formData.group);
    }
    this.updateUnifiedFormData(variationUuid, { domain: '' });
    this.domainValidationResults[variationUuid] = null;
    this.updateFormDataCache(variationUuid, { domain: '', validationResult: null });
  }

  private setOwnershipManualVerificationError(
    variationUuid: string,
    hasError: boolean
  ): void {
    this.ownershipManualVerificationErrors[variationUuid] = hasError;

    const formData = this.getUnifiedFormData(variationUuid);
    if (!formData?.grouped) {
      return;
    }

    this.configurableProducts.forEach(product => {
      const productFormData = this.getUnifiedFormData(product.domainFormKey);
      if (productFormData?.group === formData.group && productFormData.grouped) {
        this.ownershipManualVerificationErrors[product.domainFormKey] = hasError;
      }
    });
  }

  private markOwnershipManualVerificationRequired(
    variationUuid: string,
    group: string,
    onComplete?: (variationUuid: string, isValid: boolean) => void
  ): void {
    this.setOwnershipManualVerificationError(variationUuid, true);

    if (onComplete) {
      onComplete(variationUuid, false);
      return;
    }

    this.setDomainValidationResult(variationUuid, group, false);
    this.setLoading(false);
  }

  public isDomainRegisterConfirmationPending(variationUuid: string): boolean {
    return (
      this.showDomainRegisterQuestion &&
      this.pendingDomainRegisterVariationUuids.includes(variationUuid)
    );
  }

  private openDomainRegisterConfirmation(variationUuids: string[], group: string): void {
    this.showDomainRegisterQuestion = true;
    this.pendingDomainRegisterVariationUuids = [...variationUuids];
    this.pendingDomainRegisterGroup = group;

    this.domainRegisterPriceAmount = HOSTING_SITE_REGISTRATION_PRICE.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
    //this.domainRegisterPriceSuffix = ` ${this.i18nService.instant('components.orderContent.perYear')}`;
  }

  private closeDomainRegisterConfirmation(): void {
    this.showDomainRegisterQuestion = false;
    this.pendingDomainRegisterVariationUuids = [];
    this.pendingDomainRegisterGroup = '';
    this.domainRegisterPriceAmount = '';
    //this.domainRegisterPriceSuffix = ` ${this.i18nService.instant('components.orderContent.perYear')}`;
  }

  public onConfirmDomainRegister(): void {
    const group = this.pendingDomainRegisterGroup;
    const variationUuids = [...this.pendingDomainRegisterVariationUuids];

    this.closeDomainRegisterConfirmation();

    variationUuids.forEach(variationUuid => {
      this.setDomainValidationResult(variationUuid, group, true);
      this.includedInOrderVariationUuids[variationUuid] = true;
    });
  }

  public onDeclineDomainRegister(): void {
    const variationUuids = [...this.pendingDomainRegisterVariationUuids];

    this.closeDomainRegisterConfirmation();

    // Após recusar o registro, direcionamos o fluxo para que o usuário informe um domínio existente.
    variationUuids.forEach(variationUuid => {
      this.includedInOrderVariationUuids[variationUuid] = false;
      this.onDomainTypeChange('existing', variationUuid);
    });
  }

  public onDomainTypeChange(domainType: string, variationUuid: string): void {
    // Alguns UUIDs nao podem comprar dominio existing; força para "new".
    const baseVariationUuid = this.getUnifiedFormData(variationUuid)?.baseVariationUuid;
    const resolvedDomainType =
      baseVariationUuid && this.isExistingDomainPurchaseBlocked(baseVariationUuid)
        ? 'new'
        : (domainType as 'new' | 'existing');

    // Se o usuário troca o radio (new/existing), a pergunta de registro deve desaparecer
    if (this.showDomainRegisterQuestion) {
      this.closeDomainRegisterConfirmation();
    }

    this.updateUnifiedFormData(variationUuid, {
      domainType: resolvedDomainType,
      domain: '',
    });
    this.previousDomainTypeSelections[variationUuid] = resolvedDomainType;
    this.domainValidationResults[variationUuid] = null;
    this.setOwnershipManualVerificationError(variationUuid, false);
    this.setSelfConfirmedOwnership(variationUuid, false);
    this.setGoogleDomainNotInUseAcknowledged(variationUuid, false);
    this.includedInOrderVariationUuids[variationUuid] = false;
    this.clearSavedDomainStateForVariation(variationUuid);

    this.updateFormDataCache(variationUuid, {
      domainType: resolvedDomainType,
      domain: '',
      validationResult: null,
    });

    // Verifica se o valor foi atualizado corretamente
    const formData = this.getUnifiedFormData(variationUuid);
    
    if (formData?.grouped) {
      this.clearSavedDomainStateForGroup(formData.group);
      this.syncGroupedProducts(
        formData.group,
        {
          domainType: resolvedDomainType,
          domain: '',
        },
        variationUuid
      );
    }
  }

  // ============================================================================
  // SYNCHRONIZATION
  // ============================================================================

  private syncGroupedProducts(
    group: string,
    updates: Partial<UnifiedFormData>,
    excludeVariationUuid?: string
  ): void {
    const groupedProducts = this.configurableProducts.filter(product => {
      const formData = this.getUnifiedFormData(product.domainFormKey);
      return formData?.group === group && formData.grouped;
    });

    const productsToSync = groupedProducts.filter(
      product => product.domainFormKey !== excludeVariationUuid
    );

    productsToSync.forEach(product => {
      this.updateUnifiedFormData(product.domainFormKey, updates, { emitEvent: false });

      if (updates.domain !== undefined) {
        this.domainValidationResults[product.domainFormKey] = null;
        this.ownershipManualVerificationErrors[product.domainFormKey] = false;
        this.selfConfirmedOwnership[product.domainFormKey] = false;
        this.googleDomainNotInUseAcknowledged[product.domainFormKey] = false;
      }
    });

    if (updates.domain !== undefined) {
      this.persistDomainOwnershipFlags();
    }
  }

  private syncVisibleGroupedStateToSiblings(): void {
    this.domainGroups.forEach(group => {
      if (!this.domainGroupConfigs[group.type]) {
        return;
      }

      const visibleKey = this.getLastVariationUuidInGroup(group.type);
      const source = this.getUnifiedFormData(visibleKey);
      if (!source?.domain) {
        return;
      }

      const validationResult = this.domainValidationResults[visibleKey] ?? null;
      const selfConfirmed = this.isSelfConfirmedOwnership(visibleKey);
      const googleAck = this.isGoogleDomainNotInUseAcknowledged(visibleKey);

      this.configurableProducts.forEach(product => {
        if (product.domainFormKey === visibleKey) {
          return;
        }

        const productFormData = this.getUnifiedFormData(product.domainFormKey);
        if (productFormData?.group !== group.type || !productFormData.grouped) {
          return;
        }

        this.updateUnifiedFormData(
          product.domainFormKey,
          {
            domain: source.domain,
            domainType: source.domainType,
            grouped: true,
          },
          { emitEvent: false }
        );

        this.domainValidationResults[product.domainFormKey] = validationResult;
        this.selfConfirmedOwnership[product.domainFormKey] = selfConfirmed;
        this.googleDomainNotInUseAcknowledged[product.domainFormKey] = googleAck;
        this.updateFormDataCache(product.domainFormKey, {
          domain: source.domain,
          domainType: source.domainType,
          grouped: true,
          validationResult,
        });
      });

      this.persistDomainOwnershipFlags();
    });
  }

  public getGroupedProductsInfo(group: string): {
    count: number;
    names: string[];
  } {
    const groupedProducts = this.configurableProducts.filter(product => {
      const formData = this.getUnifiedFormData(product.domainFormKey);
      return formData?.group === group && formData.grouped;
    });

    return {
      count: groupedProducts.length,
      names: groupedProducts.map(p => p.productName),
    };
  }

  // ============================================================================
  // DOMAIN VALIDATION
  // ============================================================================

  public checkDomainAvailability(variationUuid: string): void {
    const formData = this.getUnifiedFormData(variationUuid);

    if (!formData?.domain || !formData.domainType) {
      console.warn('Domínio ou tipo de domínio não informado');
      return;
    }

    if (formData.grouped) {
      this.checkDomainAvailabilityForGroup(formData.group, formData.domain, formData.domainType);
    } else {
      this.setLoading(true, 'shared.messages.processingVerify');
      this.domainValidationResults[variationUuid] = null;

      const completeDomain = `${formData.domain}${this.getDomainSuffixByGroup(formData.group, formData.domainType)}`;

      this.domainAvailability(
        completeDomain,
        formData.group,
        variationUuid,
        formData.domainType
      );
    }
  }

  private checkDomainAvailabilityForGroup(
    group: string,
    domain: string,
    domainType: string
  ): void {
    const groupedProducts = this.configurableProducts.filter(product => {
      const formData = this.getUnifiedFormData(product.domainFormKey);
      return formData?.group === group && formData.grouped;
    });

    if (groupedProducts.length === 0) {
      console.warn('Nenhum produto encontrado no grupo para validação em lote');
      return;
    }

    this.setLoading(true, 'shared.messages.processingVerifyGroup', { count: groupedProducts.length });

    groupedProducts.forEach(product => {
      this.domainValidationResults[product.domainFormKey] = null;
    });

    const completeDomain = `${domain}${this.getDomainSuffixByGroup(group, domainType)}`;
    
    this.validateDomainForGroupProducts(
      completeDomain,
      group,
      groupedProducts,
      domainType
    );
  }

  private validateDomainForGroupProducts(
    completeDomain: string,
    group: string,
    products: ConfigurableProduct[],
    domainType: string
  ): void {
    const pendingDomainRegisterVariationUuids: string[] = [];
    let completedValidations = 0;
    let allValidationsSuccessful = true;
    let hasAuthorizationRequired = false;
    let pendingValidationParams: {
      variationUuid: string;
      group: string;
      isValid: boolean;
    } | null = null;

    const handleValidationComplete = (variationUuid: string, isValid: boolean, needAuthorization = false) => {
      completedValidations++;
      
      if (!isValid) {
        allValidationsSuccessful = false;
      }

      if (
        domainType === 'new' &&
        isValid &&
        group !== 'microsoft' &&
        !pendingDomainRegisterVariationUuids.includes(variationUuid)
      ) {
        const product = products.find(p => p.domainFormKey === variationUuid);
        if (
          product &&
          !(group !== 'google' && this.showFreeDomainLabel(product.selectedVariation)) &&
          !this.isRegistryDomainVariationUuid(product.selectedVariation?.uuid)
        ) {
          pendingDomainRegisterVariationUuids.push(variationUuid);
        }
      }

      if (needAuthorization && !hasAuthorizationRequired) {
        hasAuthorizationRequired = true;
        pendingValidationParams = {
          variationUuid,
          group,
          isValid,
        };
      }

      if (completedValidations === products.length) {
        if (hasAuthorizationRequired && pendingValidationParams) {
          this.showAuthorizationModal = true;
          this.domainNeedingAuthorization = completeDomain;
          this.pendingValidationParams = pendingValidationParams;
        } else if (pendingDomainRegisterVariationUuids.length > 0) {
          this.openDomainRegisterConfirmation(pendingDomainRegisterVariationUuids, group);
        } else {
          products.forEach(product => {
            this.setDomainValidationResult(product.domainFormKey, group, allValidationsSuccessful);
          });
        }
        this.setLoading(false);
      }
    };

    products.forEach(product => {
      const variationUuid = product.domainFormKey;

      if (group === 'google') {
        this.validateGoogleDomain(
          completeDomain,
          variationUuid,
          group,
          domainType,
          handleValidationComplete
        );
      } else if (group === 'microsoft') {
        this.validateMicrosoftDomain(
          completeDomain,
          variationUuid,
          group,
          domainType,
          handleValidationComplete
        );
      } else {
        this.validateGenericDomain(
          completeDomain,
          variationUuid,
          group,
          domainType,
          handleValidationComplete
        );
      }
    });
  }

  private setDomainValidationResult(
    variationUuid: string,
    group: string,
    isValid: boolean
  ): void {
    this.domainValidationResults[variationUuid] = isValid;

    this.updateFormDataCache(variationUuid, {
      validationResult: isValid,
    });

    const formData = this.getUnifiedFormData(variationUuid);
    if (formData?.grouped) {
      const groupedProducts = this.configurableProducts.filter(product => {
        const productFormData = this.getUnifiedFormData(product.domainFormKey);
        return productFormData?.group === group && productFormData.grouped;
      });

      groupedProducts.forEach(product => {
        this.domainValidationResults[product.domainFormKey] = isValid;
        this.updateFormDataCache(product.domainFormKey, {
          validationResult: isValid,
        });
      });
    }
  }

  private domainAvailability(
    completeDomain: string,
    group: string,
    variationUuid: string,
    domainType: string
  ): void {
    if (group === 'google') {
      this.validateGoogleDomain(completeDomain, variationUuid, group, domainType);
    } else if (group === 'microsoft') {
      this.validateMicrosoftDomain(
        completeDomain,
        variationUuid,
        group,
        domainType
      );
    } else {
      this.validateGenericDomain(
        completeDomain,
        variationUuid,
        group,
        domainType
      );
    }
  }

  private validateGoogleDomain(
    completeDomain: string,
    variationUuid: string,
    group: string,
    domainType: string,
    onComplete?: (variationUuid: string, isValid: boolean) => void
  ): void {
    if (!this.isValidGoogleDomainFormat(completeDomain)) {
      if (onComplete) {
        onComplete(variationUuid, false);
      } else {
        this.handleValidationResult(variationUuid, group, false);
      }
      return;
    }

    this.validationService
      .checkGoogleWorkspaceDomainAvailability(completeDomain)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (domainType === 'new') {
            if (onComplete) {
              onComplete(variationUuid, response.valid);
            } else if (response.valid) {
              // Google new disponível → pergunta de registro pago (nunca grátis).
              this.openDomainRegisterConfirmation([variationUuid], group);
              this.setLoading(false);
            } else {
              this.handleValidationResult(variationUuid, group, false);
            }
            return;
          }

          // existing: valid=true no endpoint significa disponível (não existe).
          if (response.valid) {
            if (onComplete) {
              onComplete(variationUuid, false);
            } else {
              this.handleValidationResult(variationUuid, group, false);
            }
            return;
          }

          // Domínio existe: validar ownership (e-mail → CNPJ).
          this.validateExistingDomainOwnership(
            completeDomain,
            variationUuid,
            group,
            onComplete
          );
        },
        error: error => {
          console.error('Erro na validação Google:', error);
          if (onComplete) {
            onComplete(variationUuid, false);
          } else {
            this.handleValidationError(error, variationUuid, group);
          }
        },
      });
  }


  private isValidGoogleDomainFormat(domain: string): boolean {
    return isValidDomain(domain);
  }

  private validateExistingDomainOwnership(
    completeDomain: string,
    variationUuid: string,
    group: string,
    onComplete?: (variationUuid: string, isValid: boolean) => void
  ): void {
    this.userFacade
      .getUserDetails()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(user => {
        const email = user?.email;
        const document = user?.document;

        if (!email) {
          if (onComplete) {
            // Lote: evita travar o aggregator e oferece confirmação manual.
            this.markOwnershipManualVerificationRequired(
              variationUuid,
              group,
              onComplete
            );
          } else {
            this.handleValidationError(
              new Error('Email do usuário não encontrado'),
              variationUuid,
              group
            );
          }
          return;
        }

        this.validationService
          .validateDomainOwnership(completeDomain, email)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: ownershipResponse => {
              if (ownershipResponse.isValid) {
                this.setOwnershipManualVerificationError(variationUuid, false);
                if (onComplete) {
                  onComplete(variationUuid, true);
                } else {
                  this.handleValidationResult(variationUuid, group, true);
                  this.setLoading(false);
                }
                return;
              }

              if (!document) {
                this.markOwnershipManualVerificationRequired(
                  variationUuid,
                  group,
                  onComplete
                );
                return;
              }

              this.validationService
                .validateDomainOwnership(completeDomain, document)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: ownershipByDocumentResponse => {
                    const isOwnerByDocument = ownershipByDocumentResponse.isValid;

                    if (!isOwnerByDocument) {
                      this.markOwnershipManualVerificationRequired(
                        variationUuid,
                        group,
                        onComplete
                      );
                      return;
                    }

                    this.setOwnershipManualVerificationError(variationUuid, false);
                    if (onComplete) {
                      onComplete(variationUuid, true);
                    } else {
                      this.handleValidationResult(variationUuid, group, true);
                      this.setLoading(false);
                    }
                  },
                  error: ownershipByDocumentError => {
                    if (onComplete) {
                      this.markOwnershipManualVerificationRequired(
                        variationUuid,
                        group,
                        onComplete
                      );
                    } else {
                      this.handleValidationError(
                        ownershipByDocumentError,
                        variationUuid,
                        group
                      );
                    }
                  },
                });
            },
            error: ownershipError => {
              if (onComplete) {
                this.markOwnershipManualVerificationRequired(
                  variationUuid,
                  group,
                  onComplete
                );
              } else {
                this.handleValidationError(ownershipError, variationUuid, group);
              }
            },
          });
      });
  }

  private validateMicrosoftDomain(
    completeDomain: string,
    variationUuid: string,
    group: string,
    domainType: string,
    onComplete?: (variationUuid: string, isValid: boolean, needAuthorization?: boolean) => void
  ): void {
    const params = this.createMicrosoftParams(completeDomain, variationUuid, domainType);

    this.validationService
      .checkMicrosoftTenantIdAvailability(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const isValid = response.data.isAvailable;
          const needAuthorization = !isValid && domainType === 'existing';

          if (onComplete) {
            onComplete(variationUuid, isValid, needAuthorization);
          } else {
            if (needAuthorization) {
              this.showAuthorizationModal = true;
              this.domainNeedingAuthorization = completeDomain;
              this.pendingValidationParams = {
                variationUuid,
                group,
                isValid,
              };
              return;
            }

            this.handleValidationResult(variationUuid, group, isValid);
          }
        },
        error: error => {
          if (onComplete) {
            onComplete(variationUuid, false);
          } else {
            this.handleValidationError(error, variationUuid, group);
          }
        },
      });
  }

  private validateGenericDomain(
    completeDomain: string,
    variationUuid: string,
    group: string,
    domainType: string,
    onComplete?: (variationUuid: string, isValid: boolean) => void
  ): void {
    this.validationService
      .checkRegistroBrDomainAvailability(completeDomain)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: RegistroBrDomainAvailabilityResponse) => {
          const isAvailable = response.isAvailable ?? false;

          if (domainType === 'new') {
            if (!isAvailable) {
              if (onComplete) {
                onComplete(variationUuid, false);
              } else {
                this.handleValidationResult(variationUuid, group, false);
              }
              return;
            }

            if (onComplete) {
              onComplete(variationUuid, true);
            } else {
              const product = this.configurableProducts.find(
                p => p.domainFormKey === variationUuid
              );
              const shouldShowFreeDomain = this.showFreeDomainLabel(product?.selectedVariation);

              if (shouldShowFreeDomain || this.isRegistryDomainVariationUuid(product?.selectedVariation?.uuid)) {
                this.handleValidationResult(variationUuid, group, true);
              } else {
                this.openDomainRegisterConfirmation([variationUuid], group);
                this.setLoading(false);
              }
            }
            return;
          } 

          // domainType === 'existing'
          if (isAvailable) {
            if (onComplete) {
              onComplete(variationUuid, false);
            } else {
              this.handleValidationResult(variationUuid, group, false);
            }
            return;
          }

          // Domínio em uso + existing: validar ownership
          this.validateExistingDomainOwnership(
            completeDomain,
            variationUuid,
            group,
            onComplete
          );
        },
        error: _error => {
          if (_error instanceof HttpErrorResponse) {
            if (onComplete) {
              onComplete(variationUuid, false);
            } else {
              this.handleValidationError(_error, variationUuid, group);
            }
          } else {
            if (onComplete) {
              onComplete(variationUuid, false);
            } else {
              this.handleValidationError(_error, variationUuid, group);
            }
          }
        },
      });
  }

  private createMicrosoftParams(
    completeDomain: string,
    variationUuid: string,
    domainType: string
  ): MicrosoftTenantIdValidationParams {
    const product = this.configurableProducts.find(p => p.domainFormKey === variationUuid);
    const selectedVariation = product?.selectedVariation as ProductV2Variation | undefined;

    return {
      productUuid: selectedVariation?.uuid || '',
      isVariation: true,
      userUuid: this.userUuid || '',
      tenantId: completeDomain,
      tenantPreference: domainType,
    };
  }

  private handleValidationResult(
    variationUuid: string,
    group: string,
    isValid: boolean
  ): void {
    this.setDomainValidationResult(variationUuid, group, isValid);
    this.setLoading(false);
  }

  private handleValidationError(
    error: unknown,
    variationUuid: string,
    group: string
  ): void {
    if (error instanceof HttpErrorResponse) {
      const errorMessage = error.error?.message || error.error?.msg || '';
      
      // Verifica se é a mensagem específica do Microsoft que requer autorização
      if (this.isMicrosoftPartnerRelationshipError(errorMessage)) {
        this.handleMicrosoftPartnerRelationshipError(variationUuid, group);
        return;
      }
    }
    this.setDomainValidationResult(variationUuid, group, false);
    this.setLoading(false);
  }

  private isMicrosoftPartnerRelationshipError(errorMessage: string): boolean {
    if (!errorMessage) return false;
    const hasTenantIdNotAvailable = errorMessage.includes('Tenant ID is not available');
    const hasPartnerRelationship = errorMessage.includes('establish a partner relationship');
    const hasMicrosoftAdminLink = errorMessage.includes('admin.microsoft.com');
    return hasTenantIdNotAvailable && hasPartnerRelationship && hasMicrosoftAdminLink;
  }

  private handleMicrosoftPartnerRelationshipError(variationUuid: string, group: string): void {
    const formData = this.getUnifiedFormData(variationUuid);
    const completeDomain = `${formData?.domain}.onmicrosoft.com`;
    
    this.showAuthorizationModal = true;
    this.domainNeedingAuthorization = completeDomain;
    this.pendingValidationParams = {
      variationUuid,
      group,
      isValid: false,
    };

    this.setLoading(false);
  }

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    this.setLoading(true, 'shared.messages.processingProducts'); 
    this.initializeForm();

    this.cartFacade
      .getCartItems()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: items => {
          this.filterConfigurableProducts(items);
          // Se não estiver no último step, sempre inicia em modo de edição após carregar produtos
          if (!this.isLastStep && !this.isEditing) {
            this.isEditing = true;
            this.createUnifiedForms();
          }
          this.setLoading(false);
        },
        error: error => {
          console.error('Erro ao carregar itens do carrinho:', error);
          this.setLoading(false);
        },
      });

    this.userFacade
      .getUserUuid()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: uuid => {
          this.userUuid = uuid;
        },
        error: error => {
          console.error('Erro ao carregar UUID do usuário:', error);
        },
      });

      this.domainRegisterPriceSuffix$ = this.i18nService.get('shared.labels.perYear');
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Se isLastStep ou isEditing mudarem e já tiver produtos carregados, recarrega para exibir os domínios corretamente
    if ((changes['isLastStep'] || changes['isEditing']) && this.configurableProducts.length > 0) {
      this.cartFacade
        .getCartItems()
        .pipe(takeUntil(this.destroy$), take(1))
        .subscribe(items => {
          this.filterConfigurableProducts(items);
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================================
  // FORM MANAGEMENT
  // ============================================================================

  private initializeForm(): void {
    this.domainConfigForm = this.formBuilder.group({
      useSameDomain: [false],
      groupConfigs: this.formBuilder.array([]),
    });
  }

  private createUnifiedForms(): void {
    this.setLoading(true, 'shared.messages.processingConfig');

    this.restoreDomainOwnershipFlags();
    this.saveCurrentFormData();

    const groupConfigsArray = this.domainConfigForm.get(
      'groupConfigs'
    ) as FormArray;
    groupConfigsArray.clear();

    this.configurableProducts.forEach(product => {
      const variationUuid = product.domainFormKey;
      const baseVariationUuid = product.baseVariationUuid;
      const cachedData = this.formDataCache[variationUuid];
      const isCacheFromSameCartLine =
        cachedData?.['cartItemId'] === product.cartItemId;
      const effectiveCachedData = isCacheFromSameCartLine ? cachedData : undefined;
      const productGroup = this.getProductDomainType(product);
      const cachedDomainType = effectiveCachedData?.['domainType'];
      const computedDomainType: 'new' | 'existing' =
        cachedDomainType === 'existing' || cachedDomainType === 'new'
          ? cachedDomainType
          : product.domainType === 'existing' || product.domainType === 'new'
            ? product.domainType
            : this.previousDomainTypeSelections[variationUuid] === 'existing'
              ? 'existing'
              : 'new';
      const domainType = this.isExistingDomainPurchaseBlocked(baseVariationUuid)
        ? 'new'
        : computedDomainType;
      const existingDomain =
        effectiveCachedData?.['domain'] ??
        this.extractDomainNameForInput(product.domain || '', productGroup, domainType);
      const isGrouped = this.resolveIsGrouped(productGroup, effectiveCachedData);

      const unifiedForm = this.formBuilder.group({
        domainType: [domainType, Validators.required],
        domain: [
          existingDomain,
          [Validators.required, Validators.minLength(3)],
        ],
        variationUuid: [variationUuid],
        baseVariationUuid: [baseVariationUuid],
        cartItemId: [product.cartItemId],
        group: [productGroup],
        grouped: [isGrouped],
      });

      groupConfigsArray.push(unifiedForm);

      const domainControl = unifiedForm.get('domain');
      if (domainControl) {
        domainControl.valueChanges.subscribe(domain => {
          if ((domain ?? '') === (existingDomain ?? '')) {
            return;
          }
          this.markDomainPendingVerification(variationUuid);
        });
      }

      if (effectiveCachedData?.['validationResult'] !== undefined) {
        this.domainValidationResults[variationUuid] = effectiveCachedData[
          'validationResult'
        ] as boolean | null;
      } else if (product.domain && product.domain.trim().length > 0) {
        if (this.originalDomainValidationResults[variationUuid] !== undefined) {
          this.domainValidationResults[variationUuid] = this.originalDomainValidationResults[variationUuid];
        } else {
          this.domainValidationResults[variationUuid] = true;
        }
      }
    });

    this.restoreDomainOwnershipFlags();
    this.setLoading(false);
  }

  private saveCurrentFormData(): void {
    if (!this.domainConfigForm) return;

    const groupConfigsArray = this.domainConfigForm.get(
      'groupConfigs'
    ) as FormArray;

    for (let i = 0; i < groupConfigsArray.length; i++) {
      const form = groupConfigsArray.at(i);
      const variationUuid = form?.get('variationUuid')?.value;

      if (variationUuid) {
        const product = this.configurableProducts[i];
        this.formDataCache[variationUuid] = {
          domain: form?.get('domain')?.value || '',
          domainType: form?.get('domainType')?.value || 'new',
          group: form?.get('group')?.value || 'other',
          grouped: form?.get('grouped')?.value || false,
          validationResult: this.domainValidationResults[variationUuid],
          cartItemId: product?.cartItemId,
          selfConfirmedOwnership: this.isSelfConfirmedOwnership(variationUuid),
          googleDomainNotInUseAcknowledged:
            this.isGoogleDomainNotInUseAcknowledged(variationUuid),
        };
      }
    }

    if (groupConfigsArray.length > 0) {
      this.persistDomainOwnershipFlags();
    }
  }

  private updateFormDataCache(variationUuid: string, updates: Record<string, unknown>): void {
    if (!this.formDataCache[variationUuid]) {
      this.formDataCache[variationUuid] = {};
    }
    this.formDataCache[variationUuid] = {
      ...this.formDataCache[variationUuid],
      ...updates,
    };
  }

  public isFormValid(): boolean {
    if (!this.domainConfigForm) {
      return false;
    }

    // Após Continuar/Salvar o FormArray é limpo; aí a validade vem do que já foi persistido.
    if (!this.isEditing || this.groupConfigsArray.length === 0) {
      return !this.hasConfigurableProducts || this.areAllDomainsConfigured();
    }

    return this.configurableProducts.every(product => {
      // Em lote, o formulário oculto não tem o CVA no DOM e não deve bloquear Continuar.
      if (!this.checkInputVisibility(product.domainFormKey, product.domainFormKey)) {
        return true;
      }

      const formIndex = this.findFormIndexByVariationUuid(product.domainFormKey);
      if (formIndex === -1) {
        return false;
      }

      return this.groupConfigsArray.at(formIndex)?.valid === true;
    });
  }

  public areAllDomainsValid(): boolean {
    return this.configurableProducts.every(product => {
      // Em lote, só o input visível representa o grupo.
      if (!this.checkInputVisibility(product.domainFormKey, product.domainFormKey)) {
        return true;
      }

      const domainValue = this.getDomainValue(product.domainFormKey);
      const productDomain = product.domain && product.domain.trim().length > 0;
      const hasDomain = (domainValue && domainValue.trim() !== '') || productDomain;

      if (!hasDomain) {
        return false;
      }

      if (!this.isGoogleDomainAcknowledgmentSatisfied(product.domainFormKey)) {
        return false;
      }

      // Step 4 em edição: qualquer mudança (domínio, tipo ou lote) exige Verificar de novo.
      if (this.hasDomainConfigChangedSinceEditStart(product.domainFormKey)) {
        return this.domainValidationResults[product.domainFormKey] === true;
      }

      if (this.domainValidationResults[product.domainFormKey] === true) {
        return true;
      }

      const isSavedInCart =
        this.savedDomainsInCart.has(product.domainFormKey) || productDomain;
      if (isSavedInCart) {
        return true;
      }

      return false;
    });
  }

  private hasDomainConfigChangedSinceEditStart(variationUuid: string): boolean {
    if (!this.isLastStep || !this.isEditing) {
      return false;
    }

    if (this.hasDomainGroupConfigChangedSinceEditStart(variationUuid)) {
      return true;
    }

    return this.hasDomainChangedSinceEditStart(variationUuid);
  }

  private hasDomainGroupConfigChangedSinceEditStart(variationUuid: string): boolean {
    const formData = this.getUnifiedFormData(variationUuid);
    const group = formData?.group;
    if (group !== 'google' && group !== 'microsoft') {
      return false;
    }

    const hasOriginalGroupSnapshot = Object.prototype.hasOwnProperty.call(
      this.originalDomainGroupConfigs,
      group
    );
    if (!hasOriginalGroupSnapshot && Object.keys(this.originalDomainGroupConfigs).length === 0) {
      // Snapshot ainda não capturado.
      return false;
    }

    const originalGrouped = this.originalDomainGroupConfigs[group] === true;
    const currentGrouped = this.domainGroupConfigs[group] === true;
    return originalGrouped !== currentGrouped;
  }

  private hasDomainChangedSinceEditStart(variationUuid: string): boolean {
    // Snapshot só existe na edição do último step; no step 3 não força revalidação.
    if (!this.isLastStep || !this.isEditing) {
      return false;
    }

    const hasOriginalSnapshot = Object.prototype.hasOwnProperty.call(
      this.originalDomains,
      variationUuid
    );
    if (!hasOriginalSnapshot) {
      return false;
    }

    const currentDomain = (this.getDomainValue(variationUuid) ?? '').trim();
    const currentDomainType = this.getDomainTypeValue(variationUuid) as 'new' | 'existing';
    const originalDomain = (this.originalDomains[variationUuid] ?? '').trim();
    const originalDomainType = this.originalDomainTypes[variationUuid];

    return (
      currentDomain !== originalDomain ||
      (originalDomainType !== undefined && currentDomainType !== originalDomainType)
    );
  }

  private markDomainPendingVerification(variationUuid: string): void {
    this.domainValidationResults[variationUuid] = null;
    this.updateFormDataCache(variationUuid, { validationResult: null });

    const formData = this.getUnifiedFormData(variationUuid);
    if (!formData?.grouped) {
      return;
    }

    this.configurableProducts.forEach(product => {
      const productFormData = this.getUnifiedFormData(product.domainFormKey);
      if (productFormData?.group === formData.group && productFormData.grouped) {
        this.domainValidationResults[product.domainFormKey] = null;
        this.updateFormDataCache(product.domainFormKey, { validationResult: null });
      }
    });
  }

  private isGoogleDomainAcknowledgmentSatisfied(variationUuid: string): boolean {
    if (!this.shouldShowGoogleDomainNotInUseWarning(variationUuid)) {
      return true;
    }

    return this.isGoogleDomainNotInUseAcknowledged(variationUuid);
  }

  // ============================================================================
  // DATA PROCESSING
  // ============================================================================

  private initializeSavedDomainsState(): void {
    this.savedDomainsInCart.clear();
    this.configurableProducts.forEach(product => {
      if (product.domain && product.domain.trim().length > 0) {
        this.savedDomainsInCart.add(product.domainFormKey);
      }
    });
  }

  private pruneStaleRecordKeys<T>(
    record: Record<string, T>,
    activeKeys: Set<string>,
    preservePrivateKeys = false
  ): void {
    Object.keys(record).forEach(key => {
      if (preservePrivateKeys && key.startsWith('_')) {
        return;
      }

      if (!activeKeys.has(key)) {
        delete record[key];
      }
    });
  }

  private pruneStaleSetKeys(keys: Set<string>, activeKeys: Set<string>): void {
    keys.forEach(key => {
      if (!activeKeys.has(key)) {
        keys.delete(key);
      }
    });
  }

  private pruneStaleDomainState(): void {
    const activeKeys = new Set(this.configurableProducts.map(product => product.domainFormKey));
    const domainStateRecords: Record<string, unknown>[] = [
      this.domainValidationResults,
      this.ownershipManualVerificationErrors,
      this.selfConfirmedOwnership,
      this.googleDomainNotInUseAcknowledged,
      this.previousDomainTypeSelections,
      this.includedInOrderVariationUuids,
      this.originalDomains,
      this.originalDomainTypes,
      this.originalDomainValidationResults,
      this.originalSelfConfirmedOwnership,
      this.originalGoogleDomainNotInUseAcknowledged,
    ];

    this.pruneStaleRecordKeys(this.formDataCache, activeKeys, true);
    domainStateRecords.forEach(record => this.pruneStaleRecordKeys(record, activeKeys));

    this.pruneStaleSetKeys(this.savedDomainsInCart, activeKeys);

    this.pendingDomainRegisterVariationUuids = this.pendingDomainRegisterVariationUuids.filter(key =>
      activeKeys.has(key)
    );

    if (this.showDomainRegisterQuestion && this.pendingDomainRegisterVariationUuids.length === 0) {
      this.closeDomainRegisterConfirmation();
    }
  }

  private filterConfigurableProducts(cartItems: CartItem[]): void {
    this.setLoading(true, 'shared.messages.processingProducts');
    this.configurableProducts = cartItems
      .filter(item => this.hasRequiredDomainFields(item))
      .flatMap(item => {
        const baseVariationUuid = item.selectedVariation?.uuid ?? item.product.uuid;
        const instanceCount = this.isPresencaDigitalVariationUuid(baseVariationUuid)
          ? item.quantity
          : 1;

        return Array.from({ length: instanceCount }, (_, domainFormIndex) =>
          this.mapToConfigurableProduct(item, domainFormIndex)
        );
      })
      .sort((a, b) => this.sortByGroup(a, b));

    this.pruneStaleDomainState();
    this.initializeSavedDomainsState();

    this.updateDomainGroups();
    this.createUnifiedForms();
    this.setLoading(false);
  }

  private sortByGroup(a: ConfigurableProduct, b: ConfigurableProduct): number {
    const groupA = this.getProductDomainType(a);
    const groupB = this.getProductDomainType(b);
    const groupOrder = [
      'google',
      'microsoft',
      'com.br',
      'net',
      'com',
      'unknown',
    ];
    const indexA = groupOrder.indexOf(groupA);
    const indexB = groupOrder.indexOf(groupB);

    if (indexA !== -1 && indexB !== -1) {
      if (indexA !== indexB) return indexA - indexB;
      // Tie-breaker para manter ordenação consistente em duplicações
      if (a.baseVariationUuid === b.baseVariationUuid) {
        return a.domainFormIndex - b.domainFormIndex;
      }
      return 0;
    }
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    return groupA.localeCompare(groupB);
  }

  private isPresencaDigitalVariationUuid(variationUuid?: string | null): boolean {
    if (!variationUuid) return false;
    return PRESENCA_DIGITAL_UUIDS.includes(variationUuid);
  }

  private hasRequiredDomainFields(item: CartItem): boolean {
    // Para Google e Microsoft: verifica no produto
    if (
      item.product?.requiredFields &&
      Array.isArray(item.product.requiredFields)
    ) {
      const hasGoogleOrMicrosoft = item.product.requiredFields.some(
        (field: RequiredField) =>
          field.name === 'google_ws_domain' || field.name === 'microsoft_domain'
      );
      if (hasGoogleOrMicrosoft) return true;
    }

    // Para outros domínios (com.br, com, net): verifica na variação
    if (
      item.selectedVariation?.requiredFields &&
      Array.isArray(item.selectedVariation.requiredFields)
    ) {
      const hasOtherDomain = item.selectedVariation.requiredFields.some(
        (field: RequiredField) =>
          field.name === 'com_br_domain' ||
          field.name === 'com_domain' ||
          field.name === 'net_domain' ||
          field.name === EMAIL_DOMAIN_FIELD_NAME
      );
      if (hasOtherDomain) return true;
    }

    if (this.isRegistryDomainVariationUuid(item.selectedVariation?.uuid)) {
      return true;
    }

    if (isEmailEmpresarialVariationUuid(item.selectedVariation?.uuid)) {
      return true;
    }

    if (item.selectedVariation?.name) {
      const variationName = item.selectedVariation.name.toLowerCase();
      return (
        variationName.includes('.com.br') ||
        variationName.includes('.com') ||
        variationName.includes('.net')
      );
    }

    return false;
  }

  private getDomainPreferenceFromRequiredFields(
    requiredFields: RequiredField[] | undefined,
    domainIndex: number = 0
  ): 'new' | 'existing' | null {
    if (!requiredFields?.length) return null;
    const matches = requiredFields.filter(f => f.name === 'domain_preference');
    const raw = (matches[domainIndex] ?? matches[0])?.value;
    const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (v === 'new' || v === 'existing') return v;
    return null;
  }

  private getTenantPreferenceFromRequiredFields(
    requiredFields: RequiredField[] | undefined,
    domainIndex: number = 0
  ): 'new' | 'existing' | null {
    if (!requiredFields?.length) return null;
    const matches = requiredFields.filter(f => f.name === 'tenant_preference');
    const raw = (matches[domainIndex] ?? matches[0])?.value;
    const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (v === 'new' || v === 'existing') return v;
    return null;
  }

  private mapToConfigurableProduct(
    cartItem: CartItem,
    domainFormIndex: number
  ): ConfigurableProduct {
    const product = cartItem.product;    
    let isGoogleOrMicrosoft = false;
    if (product?.requiredFields && Array.isArray(product.requiredFields)) {
      isGoogleOrMicrosoft = product.requiredFields.some(
        (field: RequiredField) => 
          field.name === 'google_ws_domain' || field.name === 'microsoft_domain'
      );
    }
    
    const requiredFields = 
      isGoogleOrMicrosoft
        ? product.requiredFields || []
        : cartItem.selectedVariation?.requiredFields || [];
    
    const existingDomain = this.getDomainFromRequiredFields(
      product,
      cartItem.selectedVariation,
      true,
      domainFormIndex
    );

    const baseVariationUuid = cartItem.selectedVariation?.uuid ?? product.uuid;

    // Importante: ao recriar o FormArray (ex.: após mudanças no carrinho),
    // precisamos reidratar a seleção dos radiobuttons a partir do estado persistido.
    // Sem isso, o form voltaria para "new" e aparentaria "reset" ao entrar em edição.
    const preferenceFromFields =
      this.getTenantPreferenceFromRequiredFields(requiredFields, domainFormIndex) ??
      this.getDomainPreferenceFromRequiredFields(requiredFields, domainFormIndex);

    const configurableProduct: ConfigurableProduct = {
      ...product,
      id: product.uuid,
      productName: product.name,
      planName: cartItem.selectedVariation?.name || 'Plano padrão',
      selectedVariation: cartItem.selectedVariation as ProductV2Variation,
      baseVariationUuid,
      cartItemId: cartItem.id,
      domainFormIndex,
      domainFormKey: `${baseVariationUuid}__${cartItem.id}__${domainFormIndex}`,
      domainLabel: existingDomain
        ? 'pages.productConfig.domain.configured'
        : 'pages.productConfig.domain.notConfigured',
      domain: existingDomain,
      domainType: preferenceFromFields ?? 'new',
      hasRequiredFields: true,
      requiredFields: requiredFields,
    };

    configurableProduct.icon = this.categoryMapper.mapProductToIcon(
      product.categories || [],
      product.productType
    );

    return configurableProduct;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  public getGroupLabel(groupType: string): string {
    switch (groupType) {
      case 'google':
        return 'Google';
      case 'microsoft':
        return 'Microsoft';
      default:
        return 'Produto';
    }
  }

  public getProductUuid(product: ConfigurableProduct): string {
    return product.id;
  }

  public hasGroupConfig(): boolean {
    return (
      this.domainGroupConfigs['google'] || this.domainGroupConfigs['microsoft']
    );
  }

  public getDomainGroups(): { type: string; count: number; label: string }[] {
    return this.domainGroups;
  }

  public checkInputVisibility(variationUuid: string, variationUuidParam: string): boolean {
    const formData = this.getUnifiedFormData(variationUuid);

    if (!formData?.grouped) {
      return true;
    }

    const groupType = formData.group;
    const lastProductUuid = this.getLastProductUuidInGroup(groupType);
    const lastVariationUuid = this.getLastVariationUuidInGroup(groupType);

    return variationUuid === lastProductUuid && variationUuidParam === lastVariationUuid;
  }

  private getLastVariationUuidInGroup(groupType: string): string {
    const products = this.configurableProducts.filter(
      product => this.getProductDomainType(product) === groupType
    );
    return products[products.length - 1]?.domainFormKey || '';
  }

  private getLastProductUuidInGroup(groupType: string): string {
    const products = this.configurableProducts.filter(
      product => this.getProductDomainType(product) === groupType
    );
    return products[products.length - 1]?.domainFormKey || '';
  }

  // ============================================================================
  // DOMAIN MANAGEMENT
  // ============================================================================

  private updateDomainGroups(): void {
    const groups = new Map<string, number>();

    this.configurableProducts.forEach(product => {
      const domainType = this.getProductDomainType(product);

      if (domainType === 'google') {
        groups.set('google', (groups.get('google') || 0) + 1);
      } else if (domainType === 'microsoft') {
        groups.set('microsoft', (groups.get('microsoft') || 0) + 1);
      }
    });

    this.domainGroups = [];

    groups.forEach((count, type) => {
      if (count > 1 && (type === 'google' || type === 'microsoft')) {
        this.domainGroups.push({
          type,
          count,
          label: this.getDomainTypeLabel(type),
        });
        if (!(type in this.domainGroupConfigs)) {
          const cachedConfigs = this.formDataCache['_domainGroupConfigs'] as
            | Record<string, boolean>
            | undefined;
          this.domainGroupConfigs[type] = cachedConfigs?.[type] === true;
        }
      }
    });
  }

  private getDomainTypeLabel(type: string): string {
    switch (type) {
      case DOMAIN_TYPES.GOOGLE:
        return 'Google';
      case DOMAIN_TYPES.MICROSOFT:
        return 'Microsoft';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  }

  private getDomainFromRequiredFields(
    product: ProductV2,
    selectedVariation?: ProductV2Variation,
    forDisplay: boolean = false,
    domainIndex: number = 0
  ): string {
    // Para Google e Microsoft: busca no produto
    if (product?.requiredFields && Array.isArray(product.requiredFields)) {
      const googleOrMicrosoftFields = product.requiredFields.filter(
        (field: RequiredField) =>
          (field.name === 'google_ws_domain' || field.name === 'microsoft_domain') &&
          field.value &&
          field.value.trim().length > 0
      );

      if (googleOrMicrosoftFields.length > 0) {
        const domainField = googleOrMicrosoftFields[domainIndex];
        if (!domainField) return '';
        const domainValue = domainField.value || '';
        const isGoogle = domainField.name === 'google_ws_domain';

        if (domainValue && domainValue.trim().length > 0) {
          if (forDisplay || (this.isLastStep && !this.isEditing)) {
            return domainValue;
          }
          const preference = product.requiredFields.find(
            (field: RequiredField) => field.name === 'domain_preference'
          )?.value;
          return this.extractDomainNameForInput(
            domainValue,
            isGoogle ? 'google' : 'microsoft',
            isGoogle ? preference : undefined
          );
        }
      }
    }

    // Para outros domínios (com.br, com, net): busca na variação
    if (selectedVariation?.requiredFields && Array.isArray(selectedVariation.requiredFields)) {
      const otherDomainFields = selectedVariation.requiredFields.filter(
        (field: RequiredField) =>
          (field.name === 'com_br_domain' ||
          field.name === 'com_domain' ||
          field.name === 'net_domain' ||
          field.name === EMAIL_DOMAIN_FIELD_NAME) &&
          field.value &&
          field.value.trim().length > 0
      );

      if (otherDomainFields.length > 0) {
        const domainField = otherDomainFields[domainIndex];
        if (!domainField) return '';
        const domainValue = domainField.value || '';

        if (domainValue && domainValue.trim().length > 0) {
          if (forDisplay || (this.isLastStep && !this.isEditing)) {
            return domainValue;
          }
          return this.extractDomainNameForInput(domainValue);
        }
      }
    }

    return '';
  }

  private extractDomainNameForInput(
    domainValue: string,
    group?: string,
    domainType?: 'new' | 'existing' | string | null
  ): string {
    if (!domainValue) return '';

    if (group === 'google') {
      // new: UI usa label + suffix .com.br; existing: hostname completo no input
      if (domainType === 'new' && domainValue.toLowerCase().endsWith('.com.br')) {
        return domainValue.slice(0, -'.com.br'.length);
      }
      return domainValue;
    }

    if (domainValue.includes('.onmicrosoft.com')) {
      return domainValue.replace('.onmicrosoft.com', '');
    } else if (domainValue.includes('.com.br')) {
      return domainValue.replace('.com.br', '');
    } else if (domainValue.includes('.net')) {
      return domainValue.replace('.net', '');
    } else if (domainValue.includes('.com')) {
      return domainValue.replace('.com', '');
    }

    return domainValue;
  }

  // ============================================================================
  // PRODUCT MANAGEMENT
  // ============================================================================

  public getProductIcon(product: ConfigurableProduct) {
    return product.icon || { name: 'produto', type: 'mondrian-icons' as const };
  }

  public hasDomainType(
    product: ConfigurableProduct,
    domainType: string
  ): boolean {
    return this.getProductDomainType(product) === domainType;
  }

  public hasMicrosoftDomain(product: ConfigurableProduct): boolean {
    return this.hasDomainType(product, 'microsoft');
  }

  public hasGoogleDomain(product: ConfigurableProduct): boolean {
    return this.hasDomainType(product, 'google');
  }

  public hasComBrDomain(product: ConfigurableProduct): boolean {
    return this.hasDomainType(product, 'com.br');
  }

  public hasNetDomain(product: ConfigurableProduct): boolean {
    return this.hasDomainType(product, 'net');
  }

  public hasComDomain(product: ConfigurableProduct): boolean {
    return this.hasDomainType(product, 'com');
  }

  public getProductDomainType(
    product: ConfigurableProduct
  ): 'google' | 'microsoft' | 'com.br' | 'net' | 'com' | 'unknown' {
    const productRequiredFields = product.requiredFields || [];
    const hasGoogle = productRequiredFields.some(
      (field: RequiredField) => field.name === 'google_ws_domain'
    );
    const hasMicrosoft = productRequiredFields.some(
      (field: RequiredField) => field.name === 'microsoft_domain'
    );

    if (hasGoogle) return 'google';
    if (hasMicrosoft) return 'microsoft';

    // Para outros domínios: busca na variação
    const variationRequiredFields = product.selectedVariation?.requiredFields || [];
    const hasComBr = variationRequiredFields.some(
      (field: RequiredField) =>
        field.name === 'com_br_domain' || field.name === EMAIL_DOMAIN_FIELD_NAME
    );
    const hasCom = variationRequiredFields.some(
      (field: RequiredField) => field.name === 'com_domain'
    );
    const hasNet = variationRequiredFields.some(
      (field: RequiredField) => field.name === 'net_domain'
    );

    if (hasComBr) return 'com.br';
    if (hasCom) return 'com';
    if (hasNet) return 'net';

    if (this.isRegistryDomainVariationUuid(product.baseVariationUuid)) {
      return 'com.br';
    }

    if (isEmailEmpresarialVariationUuid(product.baseVariationUuid)) {
      return 'com.br';
    }

    const variationName = product.selectedVariation?.name?.toLowerCase() ?? '';
    if (variationName.includes('.com.br')) return 'com.br';
    if (variationName.includes('.net')) return 'net';
    if (variationName.includes('.com')) return 'com';

    return 'unknown';
  }

  // ============================================================================
  // CHECKBOX STATE MANAGEMENT
  // ============================================================================

  private saveDomainGroupConfigsToCache(): void {
    this.formDataCache['_domainGroupConfigs'] = { ...this.domainGroupConfigs };
  }

  private restoreDomainGroupConfigsFromCache(): void {
    const cachedConfigs = this.formDataCache['_domainGroupConfigs'] as Record<string, boolean> | undefined;
    if (cachedConfigs) {
      this.domainGroupConfigs = { ...cachedConfigs };
    }
  }

  private resolveIsGrouped(
    productGroup: string,
    cachedData?: Record<string, unknown>
  ): boolean {
    if (productGroup in this.domainGroupConfigs) {
      return this.domainGroupConfigs[productGroup] === true;
    }

    const cachedGroupConfigs = this.formDataCache['_domainGroupConfigs'] as
      | Record<string, boolean>
      | undefined;
    if (cachedGroupConfigs && productGroup in cachedGroupConfigs) {
      return cachedGroupConfigs[productGroup] === true;
    }

    return cachedData?.['grouped'] === true;
  }

  // ============================================================================
  // EDIT MODE MANAGEMENT
  // ============================================================================

  private saveOriginalValues(): void {
    this.configurableProducts.forEach(product => {
      const variationUuid = product.domainFormKey;
      const formData = this.getUnifiedFormData(variationUuid);
      const group = formData?.group || this.getProductDomainType(product);
      const originalDomainType: 'new' | 'existing' =
        formData?.domainType || product.domainType || 'new';
      const originalDomain = (
        formData?.domain ||
        this.extractDomainNameForInput(product.domain || '', group, originalDomainType)
      ).trim();
      
      this.originalDomains[variationUuid] = originalDomain;
      this.originalDomainTypes[variationUuid] = originalDomainType;
      this.originalDomainValidationResults[variationUuid] = this.domainValidationResults[variationUuid] ?? null;
      this.originalSelfConfirmedOwnership[variationUuid] =
        this.isSelfConfirmedOwnership(variationUuid);
      this.originalGoogleDomainNotInUseAcknowledged[variationUuid] =
        this.isGoogleDomainNotInUseAcknowledged(variationUuid);
    });
    
    this.originalDomainGroupConfigs = { ...this.domainGroupConfigs };
    this.originalPreviousDomainTypeSelections = { ...this.previousDomainTypeSelections };
  }

  private restoreOriginalValues(): void {
    this.configurableProducts.forEach(product => {
      const variationUuid = product.domainFormKey;
      
      if (this.originalDomains[variationUuid] !== undefined) {
        const originalDomain = this.originalDomains[variationUuid];
        const formData = this.getUnifiedFormData(variationUuid);
        const group = formData?.group || this.getProductDomainType(product);
        
        if (originalDomain) {
          product.domain = `${originalDomain}${this.getDomainSuffixByGroup(
            group,
            this.originalDomainTypes[variationUuid]
          )}`;
        } else {
          product.domain = '';
        }
        
        if (this.isEditing) {
          this.updateUnifiedFormData(variationUuid, { domain: originalDomain });
        }
      }
      
      if (this.originalDomainTypes[variationUuid] !== undefined) {
        product.domainType = this.originalDomainTypes[variationUuid];
        
        if (this.isEditing) {
          this.updateUnifiedFormData(variationUuid, { domainType: this.originalDomainTypes[variationUuid] });
        }
      }
      
      if (this.originalDomainValidationResults[variationUuid] !== undefined) {
        this.domainValidationResults[variationUuid] = this.originalDomainValidationResults[variationUuid];
      }

      if (this.originalSelfConfirmedOwnership[variationUuid] !== undefined) {
        this.selfConfirmedOwnership[variationUuid] =
          this.originalSelfConfirmedOwnership[variationUuid] === true;
      }

      if (this.originalGoogleDomainNotInUseAcknowledged[variationUuid] !== undefined) {
        this.googleDomainNotInUseAcknowledged[variationUuid] =
          this.originalGoogleDomainNotInUseAcknowledged[variationUuid] === true;
      }
    });
    
    this.domainGroupConfigs = { ...this.originalDomainGroupConfigs };
    this.previousDomainTypeSelections = { ...this.originalPreviousDomainTypeSelections };
    this.persistDomainOwnershipFlags();
    
    if (this.isEditing) {
      this.createUnifiedForms();
    }
  }

  private clearOriginalValues(): void {
    this.originalDomains = {};
    this.originalDomainTypes = {};
    this.originalDomainGroupConfigs = {};
    this.originalDomainValidationResults = {};
    this.originalSelfConfirmedOwnership = {};
    this.originalGoogleDomainNotInUseAcknowledged = {};
    this.originalPreviousDomainTypeSelections = {};
  }

  private clearTransientFormState(): void {
    this.pruneStaleRecordKeys(this.formDataCache, new Set<string>(), true);

    const groupConfigsArray = this.domainConfigForm?.get('groupConfigs') as FormArray | null;
    groupConfigsArray?.clear();
  }

  public editItem(variationUuid: string): void {
    const product = this.configurableProducts.find(p => p.selectedVariation.uuid === variationUuid);
    if (!product) {
      return;
    }

    this.restoreDomainGroupConfigsFromCache();
    this.restoreDomainOwnershipFlags();

    this.isEditing = true;
    this.setLoading(true, 'shared.messages.processingEdit');
    this.createUnifiedForms();
    this.restoreDomainOwnershipFlags();

    if (this.isLastStep) {
      this.saveOriginalValues();
    }
  }

  public cancelEdit(): void {
    // Se estiver no último step, restaurar os valores originais antes de sair do modo de edição
    if (this.isLastStep) {
      this.restoreOriginalValues();
    }
    
    this.isEditing = false;
    this.saveDomainGroupConfigsToCache();
    this.clearTransientFormState();
    this.domainGroupConfigs = {};
    this.previousDomainTypeSelections = {};
    this.initializeSavedDomainsState();
  }

  public saveAndContinue(): void {
    this.syncVisibleGroupedStateToSiblings();

    if (!this.isFormValid()) {
      console.error('Formulário inválido');
      return;
    }

    if (!this.areAllDomainsValid()) {
      console.error('Nem todos os domínios estão válidos');
      return;
    }

    this.setLoading(true, 'shared.messages.processingSave');
    this.saveCurrentFormData();
    this.saveDomainGroupConfigsToCache();

    this.saveValidatedDomainsToCart(() => {
      this.validateOrderRequest.emit({ isLastStep: this.isLastStep });
    });
  }

  public completeSaveAfterValidate(): void {
    this.clearOriginalValues();
    this.cancelEdit();
    this.setLoading(false);
  }

  public completeAndGoToNextStep(): void {
    this.clearOriginalValues();
    this.cancelEdit();
    this.setLoading(false);
    this.nextStep.emit();
  }

  public setValidateOrderError(): void {
    this.setLoading(false);
  }

  // ============================================================================
  // DOMAIN AUTHORIZATION LOGIC
  // ============================================================================

  private getDomainsNeedingAuthorization(): string[] {
    const domainsNeedingAuth: string[] = [];

    this.configurableProducts.forEach(product => {
      const formData = this.getUnifiedFormData(product.domainFormKey);

      if (
        formData?.domainType === 'existing' &&
        formData?.domain &&
        formData?.group === 'microsoft'
      ) {
        const completeDomain = `${formData.domain}.onmicrosoft.com`;
        domainsNeedingAuth.push(completeDomain);
      }
    });

    return domainsNeedingAuth;
  }

  // ============================================================================
  // CART PERSISTENCE
  // ============================================================================

  private saveValidatedDomainsToCart(callback?: () => void): void {
    // Busca os itens do carrinho uma única vez para atualizar
    this.cartFacade.getCartItems().pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (cartItems: CartItem[]) => {
        const updates: { itemId: string; product: ProductV2; selectedVariation?: ProductV2Variation }[] = [];

        // Para PRESENCA_DIGITAL_UUIDS com quantity > 1, precisamos persistir N domínios no `cart`
        // para que o `lastStep` consiga reconstruir os inputs em uma nova instância.
        const presencaMultiByBaseVariationUuid = new Map<string, ConfigurableProduct[]>();
        this.configurableProducts.forEach(p => {
          if (!this.isPresencaDigitalVariationUuid(p.baseVariationUuid)) return;
          const list = presencaMultiByBaseVariationUuid.get(p.baseVariationUuid) ?? [];
          list.push(p);
          presencaMultiByBaseVariationUuid.set(p.baseVariationUuid, list);
        });

        presencaMultiByBaseVariationUuid.forEach(presencaProducts => {
          if (presencaProducts.length <= 1) return;

          const sorted = [...presencaProducts].sort(
            (a, b) => a.domainFormIndex - b.domainFormIndex
          );

          const rep = sorted[0];
          const cartItem = cartItems.find((item: CartItem) => item.id === rep.cartItemId);
          if (!cartItem) return;

          const firstFormData = this.getUnifiedFormData(rep.domainFormKey);
          if (!firstFormData) return;

          const group = firstFormData.group as string;
          const domainFieldName = this.resolveDomainFieldName(group, rep.baseVariationUuid);

          if (!domainFieldName) return;

          const domainFieldObjects: RequiredField[] = sorted
            .map(inst => {
              const fd = this.getUnifiedFormData(inst.domainFormKey);
              if (!fd?.domain) return null;
              if (this.domainValidationResults[inst.domainFormKey] !== true) return null;

              const completeDomain = `${fd.domain}${this.getDomainSuffixByGroup(fd.group, fd.domainType)}`;

              return {
                name: domainFieldName,
                label: domainFieldName.replace(/_/g, ' '),
                type: 'text',
                value: completeDomain,
              } as RequiredField;
            })
            .filter((v): v is RequiredField => Boolean(v));

          if (group === 'google' || group === 'microsoft') {
            const productRequiredFields = cartItem.product.requiredFields || [];

            let updatedProductRequiredFields: RequiredField[] = [
              ...productRequiredFields.filter(
                (field: RequiredField) => field.name !== domainFieldName
              ),
              ...domainFieldObjects,
            ];

            // Mantém/update campos auxiliares (ex.: tenant_preference/email agreement) uma única vez.
            const tenantPrefFieldName = 'tenant_preference';
            if (group === 'microsoft') {
              const tenantPrefField = productRequiredFields.find(
                (field: RequiredField) =>
                  field.name === tenantPrefFieldName && field.value
              );

              let tenantPreference =
                firstFormData.domainType || 'new';

              if (
                tenantPrefField?.value &&
                (tenantPrefField.value === 'new' ||
                  tenantPrefField.value === 'existing')
              ) {
                tenantPreference = tenantPrefField.value;
              }

              const existingTenantPrefField = updatedProductRequiredFields.find(
                (field: RequiredField) => field.name === tenantPrefFieldName
              );

              if (existingTenantPrefField) {
                updatedProductRequiredFields = updatedProductRequiredFields.map(
                  (field: RequiredField) =>
                    field.name === tenantPrefFieldName
                      ? { ...field, value: tenantPreference }
                      : field
                );
              } else {
                updatedProductRequiredFields.push({
                  name: tenantPrefFieldName,
                  label: 'Tenant Preference',
                  type: 'TEXT',
                  value: tenantPreference,
                });
              }
            } else if (group === 'google') {
              const domainPreferenceValue =
                firstFormData.domainType === 'existing' || firstFormData.domainType === 'new'
                  ? firstFormData.domainType
                  : 'new';

              updatedProductRequiredFields = [
                ...updatedProductRequiredFields.filter(
                  (field: RequiredField) => field.name !== 'domain_preference'
                ),
                {
                  name: 'domain_preference',
                  label: 'domain_preference',
                  type: 'TEXT',
                  value: domainPreferenceValue,
                },
              ];
            }

            updates.push({
              itemId: cartItem.id,
              product: {
                ...cartItem.product,
                requiredFields: updatedProductRequiredFields,
              },
              selectedVariation: cartItem.selectedVariation,
            });
          } else {
            const variationRequiredFields = cartItem.selectedVariation?.requiredFields || [];

            let updatedVariationRequiredFields: RequiredField[];

            if (this.shouldPersistDomainPreference(domainFieldName)) {
              const domainPreferenceObjects: RequiredField[] = sorted
                .map(inst => {
                  const fd = this.getUnifiedFormData(inst.domainFormKey);
                  if (!fd?.domain) return null;
                  if (this.domainValidationResults[inst.domainFormKey] !== true) return null;
                  const prefValue =
                    fd.domainType === 'new' || fd.domainType === 'existing'
                      ? fd.domainType
                      : 'new';
                  return {
                    name: 'domain_preference',
                    label: 'domain_preference',
                    type: 'TEXT',
                    value: prefValue,
                  } as RequiredField;
                })
                .filter((v): v is RequiredField => Boolean(v));

              updatedVariationRequiredFields = [
                ...variationRequiredFields.filter(
                  (field: RequiredField) =>
                    field.name !== domainFieldName && field.name !== 'domain_preference'
                ),
                ...domainFieldObjects,
                ...domainPreferenceObjects,
              ];
            } else {
              updatedVariationRequiredFields = [
                ...variationRequiredFields.filter(
                  (field: RequiredField) => field.name !== domainFieldName
                ),
                ...domainFieldObjects,
              ];
            }

            updates.push({
              itemId: cartItem.id,
              product: {
                ...cartItem.product,
              },
              selectedVariation: {
                ...cartItem.selectedVariation!,
                requiredFields: updatedVariationRequiredFields,
              },
            });
          }
        });

        this.configurableProducts.forEach(product => {
          const formData = this.getUnifiedFormData(product.domainFormKey);
          const isPresencaDigital = this.isPresencaDigitalVariationUuid(product.baseVariationUuid);
          const isPresencaDigitalMulti = isPresencaDigital && (presencaMultiByBaseVariationUuid.get(product.baseVariationUuid)?.length ?? 0) > 1;

          if (
            formData?.domain &&
            this.domainValidationResults[product.domainFormKey] === true
          ) {
            const completeDomain = `${formData.domain}${this.getDomainSuffixByGroup(
              formData.group,
              formData.domainType
            )}`;

            const group = formData.group as string;
            const domainFieldName = this.resolveDomainFieldName(group, product.baseVariationUuid);

            if (domainFieldName) {
              const cartItem = cartItems.find((item: CartItem) => item.id === product.cartItemId);

              if (!cartItem) return;

              // Para Google e Microsoft: atualiza no produto
              if (group === 'google' || group === 'microsoft') {
                const productRequiredFields = cartItem.product.requiredFields || [];
                const tenantPrefFieldName = 'tenant_preference';
                const existingField = productRequiredFields.find(
                  (field: RequiredField) => field.name === domainFieldName
                );

                let updatedProductRequiredFields: RequiredField[];
                if (isPresencaDigital && product.domainFormIndex === 0) {
                  // Para PRESENCA_DIGITAL_UUIDS, precisamos manter N domínios (um por instância).
                  // No 1o input, limpamos os domínios antigos para reconstruir em ordem.
                  updatedProductRequiredFields = [
                    ...productRequiredFields.filter((field: RequiredField) => field.name !== domainFieldName),
                    {
                      name: domainFieldName,
                      label: domainFieldName.replace(/_/g, ' '),
                      type: 'text',
                      value: completeDomain
                    },
                  ];
                } else if (existingField) {
                  if (isPresencaDigital && product.domainFormIndex > 0) {
                    // Para as instâncias adicionais, armazenamos como outro required_field (mesmo name, valor diferente).
                    updatedProductRequiredFields = [
                      ...productRequiredFields,
                      {
                        ...existingField,
                        value: completeDomain
                      },
                    ];
                  } else {
                    // Regra padrão: 1 campo por name (substitui).
                    updatedProductRequiredFields = productRequiredFields.map((field: RequiredField) =>
                      field.name === domainFieldName
                        ? { ...field, value: completeDomain }
                        : field
                    );
                  }
                } else {
                  updatedProductRequiredFields = [
                    ...productRequiredFields,
                    {
                      name: domainFieldName,
                      label: domainFieldName.replace(/_/g, ' '),
                      type: 'text',
                      value: completeDomain
                    }
                  ];
                }

                 // Busca o domainType do formData, se não tiver, busca do cache ou produto
                 const preferenceValue =
                   formData?.domainType ||
                   this.formDataCache[product.domainFormKey]?.['domainType'] ||
                   product.domainType ||
                   'new';

                if (group === 'microsoft') {
                  const existingTenantPrefField = updatedProductRequiredFields.find(
                    (field: RequiredField) => field.name === tenantPrefFieldName
                  );

                  if (existingTenantPrefField) {
                    updatedProductRequiredFields = updatedProductRequiredFields.map(
                      (field: RequiredField) =>
                        field.name === tenantPrefFieldName
                          ? { ...field, value: preferenceValue }
                          : field
                    );
                  } else {
                    updatedProductRequiredFields.push({
                      name: tenantPrefFieldName,
                      label: 'Tenant Preference',
                      type: 'TEXT',
                      value: preferenceValue,
                    });
                  }
                } else if (group === 'google') {
                  const domainPreferenceValue =
                    preferenceValue === 'existing' || preferenceValue === 'new'
                      ? preferenceValue
                      : 'new';

                  const existingDomainPrefField = updatedProductRequiredFields.find(
                    (field: RequiredField) => field.name === 'domain_preference'
                  );

                  if (existingDomainPrefField) {
                    updatedProductRequiredFields = updatedProductRequiredFields.map(
                      (field: RequiredField) =>
                        field.name === 'domain_preference'
                          ? { ...field, value: domainPreferenceValue }
                          : field
                    );
                  } else {
                    updatedProductRequiredFields.push({
                      name: 'domain_preference',
                      label: 'domain_preference',
                      type: 'TEXT',
                      value: domainPreferenceValue,
                    });
                  }
                }



                if (!isPresencaDigitalMulti) {
                  updates.push({
                    itemId: cartItem.id,
                    product: {
                      ...cartItem.product,
                      requiredFields: updatedProductRequiredFields
                    },
                    selectedVariation: cartItem.selectedVariation
                  });
                }
              } else {
                // Para outros domínios: atualiza na variação
                const variationRequiredFields = cartItem.selectedVariation?.requiredFields || [];
                const existingField = variationRequiredFields.find(
                  (field: RequiredField) => field.name === domainFieldName
                );

                let updatedVariationRequiredFields: RequiredField[];
                if (isPresencaDigital && product.domainFormIndex === 0) {
                  updatedVariationRequiredFields = [
                    ...variationRequiredFields.filter((field: RequiredField) => field.name !== domainFieldName),
                    {
                      name: domainFieldName,
                      label: domainFieldName.replace(/_/g, ' '),
                      type: 'text',
                      value: completeDomain
                    },
                  ];
                } else if (existingField) {
                  if (isPresencaDigital && product.domainFormIndex > 0) {
                    updatedVariationRequiredFields = [
                      ...variationRequiredFields,
                      {
                        ...existingField,
                        value: completeDomain
                      }
                    ];
                  } else {
                    updatedVariationRequiredFields = variationRequiredFields.map((field: RequiredField) =>
                      field.name === domainFieldName
                        ? { ...field, value: completeDomain }
                        : field
                    );
                  }
                } else {
                  updatedVariationRequiredFields = [
                    ...variationRequiredFields,
                    {
                      name: domainFieldName,
                      label: domainFieldName.replace(/_/g, ' '),
                      type: 'text',
                      value: completeDomain
                    }
                  ];
                }

                if (this.shouldPersistDomainPreference(domainFieldName)) {
                  const prefValue =
                    formData.domainType === 'new' || formData.domainType === 'existing'
                      ? formData.domainType
                      : 'new';
                  const prefField = updatedVariationRequiredFields.find(
                    (field: RequiredField) => field.name === 'domain_preference'
                  );
                  if (prefField) {
                    updatedVariationRequiredFields = updatedVariationRequiredFields.map(
                      (field: RequiredField) =>
                        field.name === 'domain_preference'
                          ? { ...field, value: prefValue }
                          : field
                    );
                  } else {
                    updatedVariationRequiredFields = [
                      ...updatedVariationRequiredFields,
                      {
                        name: 'domain_preference',
                        label: 'domain_preference',
                        type: 'TEXT',
                        value: prefValue,
                      },
                    ];
                  }
                }

                if (!isPresencaDigitalMulti) {
                  updates.push({
                    itemId: cartItem.id,
                    product: {
                      ...cartItem.product
                    },
                    selectedVariation: {
                      ...cartItem.selectedVariation!,
                      requiredFields: updatedVariationRequiredFields
                    }
                  });
                }
              }
            }

            product.domain = completeDomain;
            product.domainLabel = 'pages.productConfig.domain.configured';

            if (formData.domainType) {
              product.domainType = formData.domainType;
            }

            this.savedDomainsInCart.add(product.domainFormKey);
          }
        });

        updates.forEach(update => {
          if (update.selectedVariation) {
            this.cartFacade.updateCartItem(update.itemId, {
              product: update.product,
              selectedVariation: update.selectedVariation
            });
          } else {
            // Se não houver variação, apenas atualiza o produto
            this.cartFacade.updateCartItemProduct(update.itemId, update.product);
          }
        });

        this.setLoading(false);
        
        if (callback) {
          callback();
        }
      },
      error: (error) => {
        console.error('Erro ao salvar domínios no carrinho:', error);
        this.setLoading(false);
      }
    });
  }

  // ============================================================================
  // DOMAIN EVENT HANDLERS
  // ============================================================================

  public onDomainGroupConfigChange(domainType: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.domainGroupConfigs[domainType] = checked;

    const productsOfType = this.configurableProducts.filter(
      product => this.getProductDomainType(product) === domainType
    );

    productsOfType.forEach(product => {
      this.updateUnifiedFormData(product.domainFormKey, { grouped: checked, domain: '' });
      this.domainValidationResults[product.domainFormKey] = null;
      this.ownershipManualVerificationErrors[product.domainFormKey] = false;
      this.selfConfirmedOwnership[product.domainFormKey] = false;
      this.googleDomainNotInUseAcknowledged[product.domainFormKey] = false;
      this.updateFormDataCache(product.domainFormKey, {
        domain: '',
        validationResult: null,
        grouped: checked,
      });
    });
    this.persistDomainOwnershipFlags();

    if (!checked) {
      productsOfType.forEach(product => {
        const formData = this.getUnifiedFormData(product.domainFormKey);
        if (formData?.domain && formData.domain.trim().length > 0) {
          this.validateDomainUniqueness(product.domainFormKey, formData.domain);
        }
      });
    }

    if (checked && productsOfType.length > 1) {
      const productWithDomain = productsOfType.find(product => {
        const formData = this.getUnifiedFormData(product.domainFormKey);
        return formData?.domain && formData.domain.trim().length > 0;
      });

      if (productWithDomain) {
        const sourceData = this.getUnifiedFormData(productWithDomain.domainFormKey);
        if (sourceData?.domain) {
          this.syncGroupedProducts(
            domainType,
            {
              domain: sourceData.domain,
              domainType: sourceData.domainType,
            },
            productWithDomain.domainFormKey
          );
        }
      }
    }
  }

  // ============================================================================
  // DOMAIN VALIDATION & UTILITIES
  // ============================================================================

  private validateDomainUniqueness(variationUuid: string, domain: string): void {
    if (!domain || domain.trim().length === 0) return;

    const formData = this.getUnifiedFormData(variationUuid);
    if (!formData) return;

    if (formData.grouped) {
      if (this.domainValidationResults[variationUuid] === false) {
        this.domainValidationResults[variationUuid] = null;
        this.updateFormDataCache(variationUuid, { validationResult: null });
      }
      return;
    }

    const isDuplicated = this.getDuplicateDomainCount(formData.group, domain) > 1;
    
    if (isDuplicated) {
      this.domainValidationResults[variationUuid] = false;
      this.updateFormDataCache(variationUuid, { validationResult: false });
    } else if (this.domainValidationResults[variationUuid] === false) {
      this.domainValidationResults[variationUuid] = null;
      this.updateFormDataCache(variationUuid, { validationResult: null });
    }
  }

  public isDomainDuplicated(variationUuid: string): boolean {
    const formData = this.getUnifiedFormData(variationUuid);
    if (!formData?.domain || formData.domain.trim().length === 0) return false;
    
    // Se o produto está agrupado, não há duplicação (domínios podem ser iguais)
    if (formData.grouped) return false;
    
    return this.getDuplicateDomainCount(formData.group, formData.domain) > 1;
  }

  private getDuplicateDomainCount(group: string, domain: string): number {
    if (!domain || domain.trim().length === 0) return 0;
    
    return this.configurableProducts.filter(product => {
      const formData = this.getUnifiedFormData(product.domainFormKey);
      return !formData?.grouped && 
             formData?.group === group && 
             formData?.domain && 
             formData.domain.trim().length > 0 &&
             `${formData.domain}${this.getDomainSuffixByGroup(group, formData.domainType)}` ===
               `${domain}${this.getDomainSuffixByGroup(group, formData.domainType)}`;
    }).length;
  }

  public getDomainValue(variationUuid: string): string {
    const formData = this.getUnifiedFormData(variationUuid);
    return formData?.domain || '';
  }

  public getDomainTypeValue(variationUuid: string): string {
    const formData = this.getUnifiedFormData(variationUuid);
    return formData?.domainType || 'new';
  }

  // ============================================================================
  // MODAL MANAGEMENT
  // ============================================================================

  public onCloseAuthorizationModal(): void {
    this.showAuthorizationModal = false;
    this.domainNeedingAuthorization = '';
    this.pendingValidationParams = null;
    this.setLoading(false);
  }

  public onAuthorizeDomain(): void {
    window.open('https://admin.microsoft.com/Adminportal/Home?invType=ResellerRelationship&partnerId=b1778d46-80f9-4781-b3a0-851385f3edae&msppId=0#/partners/invitation', '_blank');
    this.showAuthorizationModal = false;
    this.showAuthorizationConfirmationModal = true;
  }

  public onConfirmAuthorization(): void {
    this.showAuthorizationConfirmationModal = false;
    this.revalidateDomainAfterAuthorization();
  }

  private revalidateDomainAfterAuthorization(): void {
    if (!this.pendingValidationParams) return;

    const { variationUuid, group } = this.pendingValidationParams;
    const formData = this.getUnifiedFormData(variationUuid);
    
    if (!formData?.domain) return;

    this.setLoading(true, 'shared.messages.processingProducts');
    
    const completeDomain = `${formData.domain}${this.getDomainSuffixByGroup(group, formData.domainType)}`;
    
    this.validateMicrosoftDomain(
      completeDomain,
      variationUuid,
      group,
      formData.domainType
    );
  }

  // ============================================================================
  // DOMAIN CONFIGURATION STATUS
  // ============================================================================

  public areAllDomainsConfigured(): boolean {
    if (this.configurableProducts.length === 0) {
      return true;
    }

    return this.configurableProducts.every(product => {
      return (
        this.savedDomainsInCart.has(product.domainFormKey) ||
        (product.domain && product.domain.trim().length > 0)
      );
    });
  }
}
